import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef, memo } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { X, Eye, EyeOff, ExternalLink, Search, RefreshCw, Calendar, GripHorizontal } from 'lucide-react'
import {
  cn, toStyleObject, safeInvoke, computeDropPosition, toCssLength, toJsonStr, numericSortingFn, OVERLAY_Z_INDEX,
  isSelectTrue, normalizeCellValue, cellValuesEqual, cellToJsonStr, formatFrDate, formatFrDateTime,
  dateToIsoInput, isoInputToDate, dateTimeToIsoInput, isoInputToDateTime, stripHtml, generateId,
  HAS_WEBKIT_DATE_ICON,
} from '@/lib/utils'
import { createGeneralApi, clearListeners } from '../tbsGeneralApi'
import { useT } from '@/lib/i18n'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table'
import styles from './fields.module.css'

const DRAG_WIDTH  = 36
const ROWNUM_WIDTH = 40

const fmtMontant = v => {
  const n = parseFloat(v)
  return isNaN(n) ? (v ?? '') : n.toLocaleString('fr-FR', { minimumFractionDigits: 2 })
}

// Formatage d'une valeur de cellule pour l'affichage dans le pied de tableau (first/last) — Date/
// DateTime sont des objets Date (non affichables tels quels), les autres types passent tels quels.
function fmtSummaryValue(colType, v) {
  if (colType === 'Date') return formatFrDate(v) ?? ''
  if (colType === 'DateTime') return formatFrDateTime(v) ?? ''
  return String(v)
}

// setSummaryRenderer(code, fn) — fn(gridId, code, value) retourne du HTML brut, injecté tel quel
// (dangerouslySetInnerHTML, non échappé — même convention que setHtml()/toast()/messageBox()),
// contrairement à setColumnRenderer (cellules) dont le retour est rendu comme enfant React échappé.
function SummaryCellDisplay({ gridId, jsonCol, value }) {
  if (typeof jsonCol.summaryRenderer !== 'function') return value
  let html
  try {
    html = jsonCol.summaryRenderer(gridId, jsonCol.code, value)
  } catch (e) {
    console.error(`[TableField] Erreur dans setSummaryRenderer("${jsonCol.code}")`, e)
    return value
  }
  return <span dangerouslySetInnerHTML={{ __html: html }} />
}

function computeSummary(type, rows, colCode, colType) {
  const isNum = ['Numerique', 'Montant'].includes(colType)
  const numVals = () => rows.map(r => parseFloat(r.getValue(colCode))).filter(n => !isNaN(n))
  // Date/DateTime sont des objets Date : deux instances représentant le même instant ne sont pas
  // `===` — dédupliquer sur leur timestamp plutôt que sur la référence.
  const distinctKey = v => v instanceof Date ? v.getTime() : v
  switch (type) {
    case 'count':         return String(rows.length)
    case 'countDistinct': return String(new Set(rows.map(r => distinctKey(r.getValue(colCode)))).size)
    case 'sum': {
      const s = numVals().reduce((a, b) => a + b, 0)
      return isNum ? fmtMontant(s) : String(s)
    }
    case 'min': { const v = numVals(); return v.length ? (isNum ? fmtMontant(Math.min(...v)) : String(Math.min(...v))) : '—' }
    case 'max': { const v = numVals(); return v.length ? (isNum ? fmtMontant(Math.max(...v)) : String(Math.max(...v))) : '—' }
    case 'average': {
      const v = numVals(); if (!v.length) return '—'
      const avg = v.reduce((a, b) => a + b, 0) / v.length
      return isNum ? fmtMontant(avg) : avg.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
    }
    case 'range': {
      const v = numVals(); if (v.length < 2) return '—'
      const r = Math.max(...v) - Math.min(...v)
      return isNum ? fmtMontant(r) : String(r)
    }
    case 'first': { const v = rows.length ? rows[0].getValue(colCode) : null; return v == null ? '—' : fmtSummaryValue(colType, v) }
    case 'last':  { const v = rows.length ? rows[rows.length - 1].getValue(colCode) : null; return v == null ? '—' : fmtSummaryValue(colType, v) }
    default: return ''
  }
}

// Aplatit récursivement l'arbre de colonnes (groupes via `children`) en ne gardant que les
// feuilles, SANS filtrage de visibilité — dans l'ordre de profondeur. Une colonne masquée
// (`visible: false`, sur elle-même ou un groupe ancêtre) reste une colonne à part entière pour le
// flux JSON d'alimentation (lignes) et l'API impérative (getColumnList, getColumnsCount, getData,
// getJson, addRow, addRows, dupRow...) : seul son affichage (en-tête + cellules) est concerné,
// jamais sa présence/position dans les données.
function flattenLeafColumns(entries) {
  const result = []
  for (const entry of entries ?? []) {
    if (entry.children) result.push(...flattenLeafColumns(entry.children))
    else result.push(entry)
  }
  return result
}

// Variante réservée à la construction du modèle de colonnes TanStack (colDefs) : exclut
// uniquement les feuilles sous un groupe masqué (`visible: false` sur un ancêtre `children`) —
// jamais une feuille masquée elle-même, qui reste une colonne du modèle à part entière
// (redimensionnable, filtrable, triable...), simplement initialisée invisible (cf. l'état
// `columnVisibility`, initialisé plus bas) afin de rester listée — et réactivable — dans le
// panneau "Colonnes ▾".
function flattenModelLeafColumns(entries) {
  const result = []
  for (const entry of entries ?? []) {
    if (entry.children) {
      if (entry.visible === false) continue
      result.push(...flattenModelLeafColumns(entry.children))
    } else {
      result.push(entry)
    }
  }
  return result
}

// Le bord droit d'un en-tête de groupe coïncide exactement avec le bord droit de sa dernière
// colonne feuille descendante (le colSpan ne fait que fusionner visuellement des cellules,
// aucune largeur ne lui est propre) — redimensionner le groupe revient donc à redimensionner
// cette feuille, sans logique de répartition à écrire.
function getRightmostLeafHeader(header) {
  let h = header
  while (h.subHeaders?.length > 0) h = h.subHeaders[h.subHeaders.length - 1]
  return h
}

function buildColGroups(items) {
  const groups = []
  let current = null
  for (const item of items) {
    if (item[3]) {
      current = { groupLabel: item[1], items: [] }
      groups.push(current)
    } else {
      if (!current) { current = { groupLabel: null, items: [] }; groups.push(current) }
      current.items.push(item)
    }
  }
  return groups
}

// Types dont la doc (tables-arbres.md) ne définit aucun rendu écriture ("Rendu write : —") — même
// avec col.mode === 'write' (ou un override de cellule vers un de ces types), la cellule reste en
// CellDisplay, jamais en CellEditor. `Link` : mode ignoré volontairement (toujours lecture seule).
const READ_ONLY_EDITOR_TYPES = new Set(['Image', 'HTML', 'Link'])

// Fusionne un override de cellule (setCellEditor(colId, json), posé par rowId+code colonne) par
// dessus la colonne (déjà fusionnée avec colOverrides) — seules les clés type/maxSize/placeholder/
// items fournies à setCellEditor sont présentes dans l'override, jamais `code`/`mode`/`label`...
function resolveEffectiveCol(overridesByRowId, rowId, col) {
  const override = rowId != null ? overridesByRowId[rowId]?.[col.code] : null
  return override ? { ...col, ...override } : col
}

const CELL_CLEAR_BTN = {
  position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
  display: 'flex', alignItems: 'center', color: 'hsl(var(--muted-foreground))',
  lineHeight: 1,
}

const RESIZE_MIN_HEIGHT = 50 // px — poignée de redimensionnement (attribut `resizable`)

// `formatCell` optionnel — colonnes Date/DateTime : la valeur de cellule est un objet Date, dont
// le toString() par défaut ne correspond pas au format "JJ/MM/AAAA" affiché/attendu en filtre.
function makeMultiTokenFilterFn(formatCell) {
  const fn = (row, colId, filterValue) => {
    if (!filterValue) return true
    const tokens = String(filterValue).split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
    if (!tokens.length) return true
    const raw = row.getValue(colId)
    const cell = String((formatCell ? formatCell(raw) : raw) ?? '').toLowerCase()
    return tokens.some(t => cell.includes(t))
  }
  fn.autoRemove = v => !v || v === ''
  return fn
}
const multiTokenFilterFn = makeMultiTokenFilterFn()

const multiSelectFilterFn = (row, colId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true
  const cell = String(row.getValue(colId) ?? '')
  return filterValue.includes(cell)
}
multiSelectFilterFn.autoRemove = v => !v || v.length === 0

function PasswordCellDisplay({ value }) {
  const [show, setShow] = useState(false)
  const hasVal = value != null && value !== ''
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {show ? (value ?? '') : (hasVal ? '••••' : '')}
      {hasVal && (
        <button type="button" tabIndex={-1} onClick={() => setShow(v => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: 'hsl(var(--muted-foreground))' }}>
          {show ? <EyeOff size={10} /> : <Eye size={10} />}
        </button>
      )}
    </span>
  )
}

// Combo dynamique — `setCellEditor(colId, { type: 'Combo', proxyLoader, callback })` : mêmes
// attributs `proxyLoader` (`{ typeLoading, dynafilter }`) qu'un ComboField proxyLoader (type 4),
// mais la fonction de recherche est nommée dans `callback` (résolue via `window[callback]`, pas un
// global fixe comme `tbsDynaComboLoader`) — il n'y a pas de champ combo dédié ici, juste une
// cellule de tableau/arbre, donc l'identité de la cellule (compoName/lineNo/columnCode) est passée
// en plus de `typeLoading`/`dynafilter`/`token`. Si `proxyLoader`/`callback` absents → `<select>`
// statique habituel (voir CellEditor, branche `Combo`).
const DYNAMIC_COMBO_MIN_CHARS = 3
function DynamicComboCellEditor({ col, value, rowIndex, compoName, fireEdit, fireBeforeEdit }) {
  const t = useT()
  const [token, setToken]     = useState(value != null ? String(value) : '')
  const [prevValue, setPrevValue] = useState(value)
  const [options, setOptions] = useState([])
  const [open, setOpen]       = useState(false)
  const [anchorRect, setAnchorRect] = useState(null)
  const debounceRef = useRef(null)
  const wrapperRef  = useRef(null)
  const dropdownRef = useRef(null)

  // Resynchronise `token` sur `value` quand ce dernier change en dehors de cet éditeur (proxy
  // set()/setCellValue()...) — même raisonnement que CellEditor.
  if (prevValue !== value) {
    setPrevValue(value)
    setToken(value != null ? String(value) : '')
  }

  // Portail vers document.body (position: fixed, ancrée sur le rect de l'input) — la cellule vit
  // dans le corps scrollable du tableau (overflow-y: auto) : un dropdown positionné en absolute
  // classique y serait rogné par ce conteneur, ne laissant apparaître qu'un fragment en haut. Même
  // pattern que les sous-menus flyout de SideMenu.jsx. `dropdownRef` en plus de `wrapperRef` pour
  // le clic extérieur : le contenu du portail n'est pas un descendant DOM de `wrapperRef`.
  useEffect(() => {
    if (!open) return
    const handler = e => {
      if (wrapperRef.current?.contains(e.target)) return
      if (dropdownRef.current?.contains(e.target)) return
      setOpen(false)
    }
    // Fermeture au scroll (capture:true, capte le scroll du corps du tableau, pas seulement de
    // window) — un dropdown en position fixed ne suit pas le défilement de son ancre. Exclut le
    // scroll interne du dropdown lui-même (`overflow-y: auto`, voir .thFilterDropdown) : sinon,
    // faire défiler la liste de résultats la referme aussitôt.
    const closeOnScroll = e => { if (!dropdownRef.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('scroll', closeOnScroll, true)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('scroll', closeOnScroll, true)
    }
  }, [open])

  const handleSearch = e => {
    const t = e.target.value
    setToken(t)
    if (!t || t.length < DYNAMIC_COMBO_MIN_CHARS) { setOptions([]); setOpen(false); return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      const fn = window[col.callback]
      if (typeof fn !== 'function') {
        console.error(`[TableField] setCellEditor: callback "${col.callback}" introuvable sur window`)
        return
      }
      try {
        const items = await fn(col.proxyLoader?.typeLoading, col.proxyLoader?.dynafilter, compoName, rowIndex, col.code, t)
        setOptions(Array.isArray(items) ? items : [])
        setAnchorRect(wrapperRef.current?.getBoundingClientRect() ?? null)
        setOpen(true)
      } catch (e) {
        console.error(`[TableField] Erreur dans le callback de setCellEditor("${col.code}")`, e)
        setOptions([])
        setOpen(false)
      }
    }, 300)
  }

  const selectOption = ([code, label]) => {
    setToken(label)
    // `prevValue` alignée dès maintenant sur `code` — sans ça, le re-rendu déclenché par
    // fireEdit(code) (nouvelle valeur de cellule → nouvelle prop `value`) ferait déclencher la
    // resynchronisation `token`/`value` ci-dessus, qui écraserait le libellé qu'on vient de poser
    // par le code brut (`value` stocké est le code, pas le libellé — jamais retrouvable après coup,
    // aucune liste d'items statique à consulter pour un combo dynamique).
    setPrevValue(code)
    setOptions([])
    setOpen(false)
    fireEdit(code)
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <Search size={12} style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', color: '#8a94a6', pointerEvents: 'none' }} />
      <input type="text" value={token} placeholder={col.placeholder}
        onFocus={fireBeforeEdit}
        onChange={handleSearch}
        autoComplete="off"
        style={{ paddingLeft: 20 }}
        className={styles.cellInput} />
      {open && anchorRect && createPortal(
        <div
          ref={dropdownRef}
          className={styles.thFilterDropdown}
          style={{ position: 'fixed', left: anchorRect.left, top: anchorRect.bottom, width: anchorRect.width, zIndex: OVERLAY_Z_INDEX }}
        >
          {options.filter(item => !item[3]).length > 0
            ? options.filter(item => !item[3]).map(([code, label], i) => (
                <div key={i} className={styles.thFilterOption} onMouseDown={() => selectOption([code, label])}>{label}</div>
              ))
            : <div className={styles.thFilterEmpty}>{t('noResult')}</div>
          }
        </div>,
        document.body
      )}
    </div>
  )
}

function CellEditor({ col, value, rowIndex, onEvent, tableId, onCellChange }) {
  const [val, setVal] = useState(value ?? '')
  const [prevValue, setPrevValue] = useState(value)
  const [err, setErr] = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const dateInputRef = useRef(null)
  // showPicker() : API standard (Chrome/Edge 99+, Firefox 101+) pour ouvrir le picker natif depuis
  // un déclencheur quelconque — nécessaire ici puisque sous Chrome l'icône native est masquée (CSS
  // `.tbs-date-input`) et remplacée par ce bouton (col.type === 'Date' ci-dessous). try/catch :
  // showPicker() lève (ex. NotAllowedError) si le champ est désactivé ou hors du DOM au moment du
  // clic — fallback focus() plutôt que de laisser l'exception remonter.
  const openDatePicker = () => {
    try { dateInputRef.current?.showPicker?.() }
    catch { dateInputRef.current?.focus() }
  }
  // Resynchronise `val` sur `value` quand ce dernier change en dehors de cet éditeur (proxy
  // set()/setCellValue(), rechargement...) — sans ce pattern « adjusting state » (identique à
  // ChampField.jsx), `val` reste figé sur sa valeur d'initialisation : la cellule affiche
  // durablement l'ancienne valeur bien que get()/getData() renvoient déjà la nouvelle. Comparaison
  // par référence : `value` ne change de référence que si la donnée sous-jacente a réellement été
  // remplacée (une Date/DateTime non modifiée conserve la même instance d'un rendu à l'autre).
  if (prevValue !== value) {
    setPrevValue(value)
    setVal(value ?? '')
  }
  const fireEdit = useCallback(v => {
    const nv = normalizeCellValue(col, v)
    onCellChange?.current?.(rowIndex, col.code, nv)
    // Valeur inchangée (ex. re-sélection du même item de Combo) : pas d'onCellEdit redondant.
    if (cellValuesEqual(col, nv, value)) return
    onEvent(tableId, 'onCellEdit', rowIndex, col.code, nv)
  }, [col, value, rowIndex, onEvent, tableId, onCellChange])
  const fireBeforeEdit = useCallback(() => onEvent(tableId, 'onCellBeforeEdit', rowIndex, col.code), [col.code, rowIndex, onEvent, tableId])

  const testMask = (v) => {
    if (!col.inputMask) return true
    try { return new RegExp(col.inputMask).test(String(v)) } catch { return true }
  }

  const handleChange = (v) => {
    if (col.inputMask) {
      const re = (() => { try { return new RegExp(col.inputMask) } catch { return null } })()
      if (re) {
        const filtered = String(v).split('').filter(ch => re.test(ch)).join('')
        setVal(filtered)
        setErr(filtered !== v)
        return
      }
    }
    setVal(v)
    setErr(false)
  }

  const handleBlur = (v) => {
    const ok = testMask(v)
    setErr(!ok)
    const nv = normalizeCellValue(col, v)
    onCellChange?.current?.(rowIndex, col.code, nv)
    // Valeur inchangée (ex. focus + blur sans frappe) : pas d'onCellEdit redondant.
    if (ok && !cellValuesEqual(col, nv, value)) onEvent(tableId, 'onCellEdit', rowIndex, col.code, nv)
  }

  const clearVal = () => { setVal(''); setErr(false); fireEdit('') }

  if (col.type === 'Select')
    return <input type="checkbox" checked={isSelectTrue(value)} onChange={e => fireEdit(String(e.target.checked))} />
  if (col.type === 'Combo' && col.proxyLoader && col.callback)
    return <DynamicComboCellEditor col={col} value={value} rowIndex={rowIndex} compoName={tableId} fireEdit={fireEdit} fireBeforeEdit={fireBeforeEdit} />
  if (col.type === 'Combo')
    return (
      <select value={val} onFocus={fireBeforeEdit} onChange={e => { setVal(e.target.value); fireEdit(e.target.value) }} className={styles.cellSelect}>
        <option value="">—</option>
        {buildColGroups(col.items ?? []).map((g, gi) =>
          g.groupLabel != null
            ? (
              <optgroup key={gi} label={g.groupLabel}>
                {g.items.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
              </optgroup>
            ) : (
              g.items.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)
            )
        )}
      </select>
    )
  if (col.type === 'Date')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type="date" ref={dateInputRef} value={dateToIsoInput(val)} placeholder={col.placeholder}
          onFocus={fireBeforeEdit}
          onChange={e => { setVal(isoInputToDate(e.target.value)); setErr(false) }}
          onBlur={e => handleBlur(isoInputToDate(e.target.value))}
          // Chrome/Edge/Safari (HAS_WEBKIT_DATE_ICON) : icône native masquée (CSS `.tbs-date-input`)
          // et remplacée par le bouton calendrier ci-dessous, toujours réservé (paddingRight 18/32,
          // mêmes paliers que le double bouton œil+croix de 'Password' plus haut dans ce fichier —
          // espacement déjà éprouvé pour deux icônes superposées sans chevauchement). Firefox : son
          // icône native (non masquable) se positionne elle-même contre `padding-right` (vérifié
          // par test) — 15px quand la croix de réinitialisation est affichée pour les rapprocher,
          // comme ChampField.jsx (mc=104).
          style={HAS_WEBKIT_DATE_ICON ? { paddingRight: val ? 32 : 18 } : (val ? { paddingRight: 15 } : undefined)}
          className={`${styles.cellInput} tbs-date-input ${err ? styles.cellInputErr : ''}`} />
        {HAS_WEBKIT_DATE_ICON && (
          <button type="button" tabIndex={-1} onClick={openDatePicker} style={{ ...CELL_CLEAR_BTN, right: val ? 18 : 2 }}>
            <Calendar size={10} />
          </button>
        )}
        {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  if (col.type === 'DateTime')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type="datetime-local" ref={dateInputRef} value={dateTimeToIsoInput(val)} placeholder={col.placeholder}
          onFocus={fireBeforeEdit}
          onChange={e => { setVal(isoInputToDateTime(e.target.value)); setErr(false) }}
          onBlur={e => handleBlur(isoInputToDateTime(e.target.value))}
          // Même motif que 'Date' ci-dessus (icône native masquée/remplacée sous Chrome, padding
          // Firefox 15px) — un `<input type="datetime-local">` porte la même icône native
          // `::-webkit-calendar-picker-indicator`, avec le même seuil de largeur en dessous duquel
          // Chrome la masque (d'autant plus probable ici : contenu plus large, jour+mois+année+heure).
          style={HAS_WEBKIT_DATE_ICON ? { paddingRight: val ? 32 : 18 } : (val ? { paddingRight: 15 } : undefined)}
          className={`${styles.cellInput} tbs-date-input ${err ? styles.cellInputErr : ''}`} />
        {HAS_WEBKIT_DATE_ICON && (
          <button type="button" tabIndex={-1} onClick={openDatePicker} style={{ ...CELL_CLEAR_BTN, right: val ? 18 : 2 }}>
            <Calendar size={10} />
          </button>
        )}
        {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  if (col.type === 'Heure')
    return (
      <select value={val ?? ''} onFocus={fireBeforeEdit}
        onChange={e => { const v = e.target.value === '' ? null : Number(e.target.value); setVal(v); fireEdit(v) }}
        className={styles.cellSelect}>
        <option value="">—</option>
        {Array.from({ length: 25 }, (_, h) => <option key={h} value={h}>{h}h</option>)}
      </select>
    )
  if (col.type === 'Montant' || col.type === 'Numerique')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type="number" value={val} placeholder={col.placeholder}
          onFocus={fireBeforeEdit}
          onInput={col.maxSize != null ? e => { if (e.target.value.length > col.maxSize) e.target.value = e.target.value.slice(0, col.maxSize) } : undefined}
          onChange={e => setVal(e.target.value)}
          onBlur={e => handleBlur(e.target.value === '' ? '' : Number(e.target.value))}
          style={String(val) !== '' ? { paddingRight: 20 } : undefined}
          className={`${styles.cellInput} ${styles.cellInputRight} ${err ? styles.cellInputErr : ''}`} />
        {String(val) !== '' && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  if (col.type === 'File')
    return (
      <input type="file" onFocus={fireBeforeEdit}
        onChange={e => {
          const file = e.target.files?.[0] ?? null
          onCellChange?.current?.(rowIndex, col.code, file)
          onEvent(tableId, 'onCellEdit', rowIndex, col.code, file)
        }}
        className={styles.cellInput} />
    )
  if (col.type === 'Password')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type={showPwd ? 'text' : 'password'} value={val} maxLength={col.maxSize ?? undefined}
          autoComplete="new-password"
          onFocus={fireBeforeEdit}
          onChange={e => setVal(e.target.value)}
          onBlur={e => handleBlur(e.target.value)}
          style={val ? { paddingRight: 32 } : { paddingRight: 18 }}
          className={`${styles.cellInput} ${err ? styles.cellInputErr : ''}`} />
        <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)}
          style={{ ...CELL_CLEAR_BTN, right: val ? 18 : 2 }}>
          {showPwd ? <EyeOff size={10} /> : <Eye size={10} />}
        </button>
        {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input type="text" value={val} placeholder={col.placeholder} maxLength={col.maxSize ?? undefined}
        onFocus={fireBeforeEdit}
        onChange={e => handleChange(e.target.value)}
        onBlur={e => handleBlur(e.target.value)}
        style={val ? { paddingRight: 20 } : undefined}
        className={`${styles.cellInput} ${err ? styles.cellInputErr : ''}`} />
      {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
    </div>
  )
}

// Valeur brute d'affichage — Date/DateTime sont des objets Date, non affichables tels quels comme
// enfant React. `value` peut aussi être déjà une chaîne formatée "JJ/MM/AAAA" (valeur de
// groupement — cf. getGroupingValue sur les colDefs Date/DateTime).
function fmtDisplayValue(colType, value) {
  if (colType === 'Date') return value instanceof Date ? (formatFrDate(value) ?? '') : (value ?? '')
  if (colType === 'DateTime') return value instanceof Date ? (formatFrDateTime(value) ?? '') : (value ?? '')
  return value ?? ''
}

function CellDisplay({ col, value, rowIndex }) {
  if (typeof col.renderer === 'function') {
    try { return col.renderer(value, rowIndex, col.code) }
    catch (e) { console.error(`[TableField] Erreur dans setColumnRenderer("${col.code}")`, e); return fmtDisplayValue(col.type, value) }
  }
  if (col.type === 'Select') return isSelectTrue(value) ? '✓' : ''
  if (col.type === 'Montant') return <span style={{ display: 'block', textAlign: 'right' }}>{fmtMontant(value)}</span>
  if (col.type === 'Numerique') return <span style={{ display: 'block', textAlign: 'right' }}>{value ?? ''}</span>
  if (col.type === 'Heure') return value != null ? `${value}h` : ''
  if (col.type === 'Date' || col.type === 'DateTime') return fmtDisplayValue(col.type, value)
  if (col.type === 'Password') return <PasswordCellDisplay value={value} />
  if (col.type === 'Combo') {
    const opt = (col.items ?? []).filter(item => !item[3]).find(([val]) => String(val) === String(value))
    return opt ? opt[1] : (value ?? '')
  }
  if (col.type === 'Link') {
    if (!value) return ''
    const linkStyle = { color: 'hsl(var(--primary))', display: 'inline-flex', alignItems: 'center' }
    if (String(value).startsWith('javascript:')) {
      const code = String(value).slice('javascript:'.length)
      return <a href="#" onClick={e => { e.preventDefault(); setTimeout(() => new Function(code)(), 0) }} style={linkStyle}><ExternalLink size={14} /></a>
    }
    return <a href={value} style={linkStyle}><ExternalLink size={14} /></a>
  }
  if (col.type === 'Code') return <code>{value ?? ''}</code>
  if (col.type === 'Image') return value ? <img src={value} alt="" style={{ maxHeight: 24 }} /> : ''
  if (col.type === 'HTML' || col.type === 'Texte') return <span dangerouslySetInnerHTML={{ __html: value ?? '' }} />
  if (col.type === 'File') return value instanceof File ? value.name : (value ?? '')
  return value ?? ''
}

// Colonne éditable (`mode: 'write'`) porteuse d'un `setColumnRenderer` : CellEditor affiche
// toujours un input à l'état "au repos" pour les colonnes en écriture (pas de bascule
// affichage/édition native, contrairement à un tableur) — un renderer prioritaire sur tout le
// cycle de vie de la cellule empêcherait alors définitivement l'édition. Ici, la cellule reste
// interactive : rendu via `renderer` (CellDisplay) au repos, bascule vers le véritable éditeur de
// colonne au clic, puis retour à l'affichage rendu une fois l'édition terminée (perte de focus).
function EditableRenderedCell({ col, value, rowIndex, onEvent, tableId, onCellChange }) {
  const [editing, setEditing] = useState(false)
  const wrapperRef = useRef(null)
  useEffect(() => {
    if (editing) wrapperRef.current?.querySelector('input, select, textarea')?.focus()
  }, [editing])
  if (!editing) {
    return (
      <div onClick={() => setEditing(true)} style={{ cursor: 'text', minHeight: '100%' }}>
        <CellDisplay col={col} value={value} rowIndex={rowIndex} />
      </div>
    )
  }
  return (
    <div ref={wrapperRef} onBlur={() => setEditing(false)}>
      <CellEditor col={col} value={value} rowIndex={rowIndex} onEvent={onEvent} tableId={tableId} onCellChange={onCellChange} />
    </div>
  )
}

function MultiSelectFilter({ column, options = [] }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const selected = column.getFilterValue() ?? []

  const toggle = useCallback(value => {
    const current = column.getFilterValue() ?? []
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value]
    column.setFilterValue(next.length > 0 ? next : undefined)
  }, [column])

  const clear = useCallback(e => {
    e.stopPropagation()
    column.setFilterValue(undefined)
  }, [column])

  useEffect(() => {
    if (!open) return
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const displayLabel = selected.length === 0
    ? t('filter')
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
      : t('selectedCount', { n: selected.length })

  return (
    <div ref={ref} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`${styles.thFilterBtn} ${selected.length > 0 ? styles.thFilterBtnActive : ''}`}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected.length === 0 ? '#999' : undefined }}>
          {displayLabel}
        </span>
        {selected.length > 0
          ? <span onMouseDown={clear} style={{ display: 'flex', alignItems: 'center', color: '#666', flexShrink: 0 }}><X size={10} /></span>
          : <span style={{ display: 'flex', alignItems: 'center', color: '#999', flexShrink: 0, fontSize: 9 }}>▾</span>
        }
      </button>
      {open && (
        <div className={styles.thFilterDropdown}>
          {options.length === 0
            ? <div className={styles.thFilterEmpty}>{t('noValue')}</div>
            : options.map(opt => (
                <label key={opt.value} className={styles.thFilterOption}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                  {opt.label}
                </label>
              ))
          }
        </div>
      )}
    </div>
  )
}

// React.memo — les 3 volets d'un TabsField (forceMount) invoquent onRenderFields à chaque
// changement d'onglet, y compris pour les volets non affichés : sans memo, un tableau de
// plusieurs centaines de lignes/colonnes serait entièrement re-rendu/reconcilié à chaque clic
// alors que ses props (issues de `field`, stable en JSON) n'ont pas changé.
function TableField({
  id, label, icon, columns = [],
  paging, pageSize = 20, pageNum = 0,
  collapseGrouping,
  sortColumn, sortDir,
  collapsible = false, collapsedOnLoad = false,
  rowNumber = false, fitColumns = false,
  toolbarBtns = [], dragDropId, fixedHeight, resizable, visible,
  onEvent, compoGroup, onDataLoaded, userData,
  gridLoader, gridDragDropCallback,
  class: cssClass, style: rawStyle,
}) {
  const t = useT()
  const cssStyle = toStyleObject(rawStyle)
  // `visible: false` (JSON) → display:none posé directement sur le div [data-tbs-root] : voir
  // ChampField.jsx pour le détail (show()/hide() de tbsGeneralApi.js ciblent ce même élément).
  const visibilityStyle = visible === false ? { display: 'none' } : undefined
  const [rows, setRows]               = useState([])
  const [totalRows, setTotalRows]     = useState(0)
  const [currentPage, setCurrentPage] = useState(paging ? (pageNum ?? 0) : -1)
  const [loading, setLoading]         = useState(false)
  const [selectedRowIdx, setSelectedRowIdx] = useState(null)
  // Survol de ligne (`onMouseEnter`/`onMouseLeave` sur le `<tr>`) — uniquement nécessaire pour les
  // cellules des colonnes figées (`lock`, voir stickyTd ci-dessous) : leur fond est posé en style
  // inline (`position: sticky` + couleur calculée), qui prime toujours sur la couleur de survol CSS
  // (`.tr:hover .td`, fields.module.css) et la masquerait sinon complètement sur ces colonnes. Les
  // colonnes non figées n'en ont pas besoin (survol déjà géré en pur CSS).
  const [hoveredRowIdx, setHoveredRowIdx] = useState(null)
  const [collapsed, setCollapsed]     = useState(collapsedOnLoad)
  const [title, setTitle]             = useState(label)
  const [dragOverRowId, setDragOverRowId] = useState(null)
  const [dragOverPos, setDragOverPos]     = useState(null)

  const [sorting, setSorting]                   = useState(() =>
    sortColumn ? [{ id: sortColumn, desc: (sortDir ?? 'ASC').toUpperCase() === 'DESC' }] : []
  )
  const [columnFilters, setColumnFilters]       = useState([])
  // Colonnes masquées dans le JSON (`visible: false`) : initialement invisibles, mais présentes
  // dans le modèle TanStack (cf. flattenModelLeafColumns) — donc listées et réactivables depuis le
  // panneau "Colonnes ▾". Lecture unique au montage (comme `sorting`/`grouping` ci-dessous) : un
  // changement de `columns` après coup ne réinitialise pas cet état, piloté ensuite par l'utilisateur.
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {}
    for (const c of flattenModelLeafColumns(columns)) {
      if (c.visible === false) initial[c.code] = false
    }
    return initial
  })
  // Groupement initial — attribut `groupBy: true` porté directement par les colonnes (plus
  // l'ancien attribut `groupingColumn` au niveau du champ, supprimé) : toutes les colonnes
  // (feuilles, dans l'ordre de profondeur du JSON `columns`) qui le portent initialisent `grouping`
  // dans cet ordre — regroupement SQL-like sur plusieurs colonnes (`grouping`, état TanStack, un
  // tableau ordonné). Lecture unique au montage (comme `sorting`/`columnVisibility` ci-dessus) : un
  // changement de `columns` après coup ne réinitialise pas cet état, piloté ensuite par
  // l'utilisateur/l'API (`setGrouping`). Seules les colonnes de type groupable (`GROUPABLE_TYPES`,
  // cf. `enableGrouping` dans buildLeafColumnDef) sont effectivement prises en compte par TanStack
  // — une colonne `groupBy: true` non groupable (type incompatible) est silencieusement ignorée,
  // même tolérance que `setGrouping`.
  const [grouping, setGrouping]                 = useState(() =>
    flattenLeafColumns(columns).filter(c => c.groupBy).map(c => c.code)
  )
  const [expanded, setExpanded]                 = useState(() => collapseGrouping ? {} : true)
  const [showGroupPanel, setShowGroupPanel]     = useState(false)
  const groupPanelRef = useRef(null)
  useEffect(() => {
    if (!showGroupPanel) return
    const handler = e => { if (groupPanelRef.current && !groupPanelRef.current.contains(e.target)) setShowGroupPanel(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showGroupPanel])
  const [columnSizing, setColumnSizing]         = useState({})
  const [showColPanel, setShowColPanel]         = useState(false)
  const colPanelRef = useRef(null)
  useEffect(() => {
    if (!showColPanel) return
    const handler = e => { if (colPanelRef.current && !colPanelRef.current.contains(e.target)) setShowColPanel(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showColPanel])
  const [colOverrides, setColOverrides]         = useState({})
  // Overrides d'éditeur par CELLULE (rowId + code colonne) — setCellEditor() sur le proxy de ligne.
  // Distinct de colOverrides (par colonne, toutes les lignes) : ici seule la ligne visée est
  // affectée. { [rowId]: { [colCode]: {type, maxSize, placeholder, items} } }
  const [cellEditorOverrides, setCellEditorOverrides] = useState({})
  // Classes CSS additionnelles par ligne (addClassCss/removeClassCss sur le proxy de ligne).
  // { [rowId]: string[] }
  const [rowClassOverrides, setRowClassOverrides] = useState({})
  const [toolbarState, setToolbarState]         = useState(toolbarBtns)
  const [toolbarVisible, setToolbarVisible]     = useState(true)
  const [loadTick, setLoadTick]                 = useState(0)
  const [tableError, setTableError]             = useState(null)
  const [, setRefreshTick]                      = useState(0)

  const dataLoadedRef  = useRef(false)
  const extraParamsRef = useRef(undefined)

  const containerRef = useRef(null)
  // Zone de défilement de la grille (`.tableScroll`, même élément que cible `fixedHeight`) — distincte
  // du div racine [data-tbs-root] (titre + toolbar + grille + pagination). setHeight()/getHeight()
  // sont surchargées ci-dessous pour cibler cette ref plutôt que la racine : un `flex-col` n'étire pas
  // `.tableCard` (`overflow:hidden`) en hauteur, donc redimensionner la racine seule tronque
  // silencieusement les lignes en trop (aucun ascenseur, `.tableScroll` gardant sa hauteur naturelle)
  // au lieu de contrôler la zone de défilement comme le fait `fixedHeight` (attribut JSON).
  const scrollElRef = useRef(null)
  // `resizable` (attribut JSON) : poignée de redimensionnement en bas du tableau, cible `.tableScroll`
  // (même raison/même élément que `fixedHeight`/`setHeight()` ci-dessus) — hauteur uniquement, la
  // largeur reste pilotée par les colonnes/le conteneur parent. `resizedHeight` (px, mesuré au
  // premier drag) prime alors sur `fixedHeight` une fois posé par l'utilisateur ; `fixedHeight`
  // reste la valeur JSON d'origine, jamais réécrite (redimensionnement manuel seulement, pas
  // persisté au-delà de la session). Même schéma mousedown/mousemove/mouseup + nettoyage au
  // démontage que le splitter de CanvasField.jsx (SplitterPanes) — pas de constante partagée
  // (SPLITTER_MIN_SIZE), fichiers distincts.
  const [resizedHeight, setResizedHeight] = useState(null)
  const activeResizeHandlersRef = useRef(null)
  const handleResizeMouseDown = useCallback(e => {
    e.preventDefault()
    const el = scrollElRef.current
    if (!el) return
    const startY = e.clientY
    const startHeight = el.getBoundingClientRect().height
    const move = ev => setResizedHeight(Math.max(RESIZE_MIN_HEIGHT, startHeight + (ev.clientY - startY)))
    const up = () => {
      activeResizeHandlersRef.current = null
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
    }
    activeResizeHandlersRef.current = { move, up }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }, [])
  useEffect(() => () => {
    const active = activeResizeHandlersRef.current
    if (!active) return
    document.removeEventListener('mousemove', active.move)
    document.removeEventListener('mouseup', active.up)
  }, [])
  const [containerWidth, setContainerWidth] = useState(0)
  useLayoutEffect(() => {
    if (collapsed) return
    const el = containerRef.current
    if (el) setContainerWidth(el.clientWidth)
  }, [collapsed])

  useEffect(() => {
    if (!fitColumns || collapsed) return
    const handleResize = () => {
      const el = containerRef.current
      if (el) setContainerWidth(el.clientWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [fitColumns, collapsed])

  const rowsRef           = useRef(rows)
  const compoGroupRef     = useRef(compoGroup ?? null)
  const userDataRef       = useRef(userData ?? null)
  const selectedRowIdxRef = useRef(null)
  const columnsRef        = useRef(columns)
  // Colonnes feuilles aplaties (hors colonnes de groupement) — c'est sur cette liste que
  // s'appuient le flux JSON d'alimentation et toute l'API impérative par colonne (getColumnList,
  // getColumnsCount, getData, addRow...), jamais sur les colonnes de groupement.
  const leafColumnsRef    = useRef(flattenLeafColumns(columns))
  const colOverridesRef   = useRef({})
  const columnVisibilityRef = useRef({})
  const cellEditorOverridesRef = useRef({})
  const rowClassOverridesRef = useRef({})
  const toolbarStateRef   = useRef(toolbarBtns)
  const onCellChangeRef   = useRef(null)
  // Groupement actif ("Grouper par") — lu par moveRow() pour contraindre le déplacement d'une ligne
  // à rester à l'intérieur de son groupe (cf. plus bas). `grouping` n'est pas dans les deps de
  // l'effet d'enregistrement (`[id]` seul) : sans cette ref, moveRow() verrait une valeur figée au
  // montage, pas le groupement effectivement actif au moment de l'appel.
  const groupingRef       = useRef(grouping)
  useLayoutEffect(() => {
    rowsRef.current           = rows
    selectedRowIdxRef.current = selectedRowIdx
    columnsRef.current        = columns
    leafColumnsRef.current    = flattenLeafColumns(columns)
    colOverridesRef.current   = colOverrides
    columnVisibilityRef.current = columnVisibility
    cellEditorOverridesRef.current = cellEditorOverrides
    rowClassOverridesRef.current = rowClassOverrides
    toolbarStateRef.current   = toolbarState
    groupingRef.current       = grouping
    onCellChangeRef.current   = (rowIndex, colCode, value) => {
      setRowsSynced(prev => {
        if (rowIndex < 0 || rowIndex >= prev.length) return prev
        const col = leafColumnsRef.current.find(c => c.code === colCode)
        const next = [...prev]
        next[rowIndex] = { ...next[rowIndex], [colCode]: normalizeCellValue(col, value) }
        return next
      })
    }
  })

  // rowsRef.current n'est autrement resynchronisé qu'après le rendu suivant (useLayoutEffect
  // ci-dessus) : un appel synchrone à getData()/getSelectedRow()/getJson() juste après un
  // setRowsSynced() — par ex. depuis un handler onLoadTable/onLoad métier appelé synchrone à la fin
  // du chargement — lirait alors encore l'ancien tableau (souvent vide), avant que React n'ait
  // eu l'occasion de commit + exécuter l'effet. setRowsSynced tient rowsRef à jour immédiatement.
  const setRowsSynced = useCallback((updaterOrValue) => {
    // La fonction passée à setRows() n'est PAS garantie de s'exécuter de façon synchrone (React
    // peut la reporter à son prochain rendu) — on calcule donc `next` nous-mêmes, à partir de
    // rowsRef.current (toujours à jour via ce même mécanisme), puis on assigne la ref en premier.
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(rowsRef.current) : updaterOrValue
    rowsRef.current = next
    setRows(next)
  }, [])

  // Même schéma que setRowsSynced ci-dessus, pour colOverrides — lu par validate() via
  // colOverridesRef. Sans lui, un setColumnReadOnly()/setColumnLabel()/... suivi d'un validate()
  // synchrone verrait encore l'ancien override.
  const setColOverridesSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(colOverridesRef.current) : updaterOrValue
    colOverridesRef.current = next
    setColOverrides(next)
  }, [])

  // Même schéma, pour columnVisibility — lu par isColumnVisible() via columnVisibilityRef, et
  // utilisé par showColumn()/hideColumn() (cf. plus bas) pour répercuter immédiatement la
  // visibilité structurelle sur l'état TanStack réellement consulté par le rendu (getVisibleLeafColumns()
  // /getHeaderGroups()/getVisibleCells()) — sans quoi showColumn()/hideColumn() ne changeraient que
  // colOverrides (visibleCols, largeur, lockedOffsets) sans jamais affecter ce qui est effectivement
  // affiché. Remplace la référence directe à setColumnVisibility comme onColumnVisibilityChange.
  const setColumnVisibilitySynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(columnVisibilityRef.current) : updaterOrValue
    columnVisibilityRef.current = next
    setColumnVisibility(next)
  }, [])

  // Même schéma, pour cellEditorOverrides — lu par le cell renderer, les proxies de ligne,
  // getCellValue/setCellValue, getJson() et validate() via cellEditorOverridesRef.
  const setCellEditorOverridesSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(cellEditorOverridesRef.current) : updaterOrValue
    cellEditorOverridesRef.current = next
    setCellEditorOverrides(next)
  }, [])

  // Même schéma, pour rowClassOverrides — classes CSS additionnelles posées par ligne via
  // addClassCss()/removeClassCss() sur le proxy de ligne (makeRowProxyById/makeRowProxy).
  const setRowClassOverridesSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(rowClassOverridesRef.current) : updaterOrValue
    rowClassOverridesRef.current = next
    setRowClassOverrides(next)
  }, [])

  // Ajoute/retire une ou plusieurs classes CSS (séparées par des espaces) sur une ligne précise
  // (rowId) — addClassCss()/removeClassCss() du proxy de ligne. Pas d'effet si rowId absent
  // (ligne déjà supprimée) ou si `names` ne contient aucune classe exploitable.
  const addRowClassCss = (rowId, names) => {
    if (rowId == null) return
    const toAdd = String(names).split(' ').filter(Boolean)
    if (!toAdd.length) return
    setRowClassOverridesSynced(o => {
      const existing = new Set(o[rowId] ?? [])
      toAdd.forEach(c => existing.add(c))
      return { ...o, [rowId]: [...existing] }
    })
  }
  const removeRowClassCss = (rowId, names) => {
    if (rowId == null) return
    const toRemove = new Set(String(names).split(' ').filter(Boolean))
    if (!toRemove.size) return
    setRowClassOverridesSynced(o => {
      if (!o[rowId]) return o
      const next = o[rowId].filter(c => !toRemove.has(c))
      if (next.length === o[rowId].length) return o
      // eslint-disable-next-line no-unused-vars
      const { [rowId]: _removed, ...rest } = o
      return next.length ? { ...rest, [rowId]: next } : rest
    })
  }

  // Même schéma, pour toolbarState — lu par getListOfToolbarBtns() via toolbarStateRef. Sans lui,
  // ce getter lirait la valeur figée au montage (l'effet qui expose window.tbsComponents[id] ne
  // s'exécute qu'une fois, deps [id]) : addElemToolbar()/removeElemToolbar()/... n'y apparaîtraient
  // alors jamais.
  const setToolbarStateSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(toolbarStateRef.current) : updaterOrValue
    toolbarStateRef.current = next
    setToolbarState(next)
  }, [])

  // Sélectionne une ligne en gardant selectedRowIdxRef synchrone : lu immédiatement par
  // getSelectedRow()/getSelectedRowIndex() pendant le traitement synchrone des events déclenchés
  // juste après (onGridSelRow, onGridCellClicked, onCellBeforeEdit...) — un state React ne serait
  // répercuté qu'au rendu suivant, donc lu comme "l'ancienne" sélection sinon.
  // selectSeqRef : incrémenté à chaque appel, y compris externe (setSelectedRow()). Permet au
  // chargement onTbsGridLoader (asynchrone) de détecter qu'une sélection plus récente a eu lieu
  // pendant qu'il était en vol, et de ne pas l'écraser avec son propre reset à `null`.
  // reloadSeqBaselineRef : capturé de façon synchrone par reload() (avant même que l'effet de
  // chargement ne redémarre) — sans lui, un setSelectedRow() appelé juste après reload() dans le
  // même tick serait déjà inclus dans la valeur "de départ" lue par l'effet (qui ne s'exécute
  // qu'après le rendu suivant), et le reset l'écraserait quand même.
  const selectSeqRef          = useRef(0)
  const reloadSeqBaselineRef  = useRef(null)
  const selectRow = useCallback(rowIndex => {
    selectedRowIdxRef.current = rowIndex
    selectSeqRef.current += 1
    setSelectedRowIdx(rowIndex)
  }, [])

  // Fait défiler jusqu'à une ligne identifiée par son _rowId (stable, indépendant du tri/filtre
  // affiché — voir data-tbs-row-id posé sur chaque <tr>) — utilisé par addRow()/addRows() : le tri
  // actif (col JSON sortColumn/sortDir, ou choisi par l'utilisateur) peut placer la ligne ajoutée
  // n'importe où visuellement, sans rapport avec l'index d'insertion brut (beforeRowNum). Différé
  // via requestAnimationFrame : la ligne doit d'abord avoir été effectivement rendue (commit React
  // suivant setRowsSynced) avant de pouvoir la localiser dans le DOM.
  // Ajuste directement `scrollTop`/`scrollLeft` du conteneur interne (`.tableScroll`, seul élément
  // scrollable du tableau) plutôt qu'un `Element.scrollIntoView()` sur la ligne elle-même — celui-ci
  // fait défiler **tous** les ancêtres scrollables nécessaires pour amener l'élément à l'écran, y
  // compris un canvas englobant (`fixedHeight`+`overflowY:auto`) ou la page : la ligne ajoutée
  // provoquait alors un scroll du conteneur, pas seulement du tableau. Aucun repli sur
  // `scrollIntoView` si `.tableScroll` est introuvable — l'objectif est justement de ne jamais
  // scroller autre chose que le tableau lui-même.
  const scrollRowIntoView = useCallback(rowId => {
    // Double rAF : une seule frame ne suffit pas toujours à garantir que le commit React de la
    // nouvelle ligne a été peint (observé notamment quand le tableau est imbriqué dans un canvas
    // lui-même scrollable) — la ligne n'existe alors pas encore dans le DOM au moment du calcul.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const rowEl = document.querySelector(`[data-tbs-root="${id}"] [data-tbs-row-id="${rowId}"]`)
        const container = rowEl?.closest(`.${styles.tableScroll}`)
        if (!rowEl || !container) return
        const rowRect = rowEl.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        if (rowRect.top < containerRect.top) {
          container.scrollTop -= containerRect.top - rowRect.top
        } else if (rowRect.bottom > containerRect.bottom) {
          container.scrollTop += rowRect.bottom - containerRect.bottom
        }
        if (rowRect.left < containerRect.left) {
          container.scrollLeft -= containerRect.left - rowRect.left
        } else if (rowRect.right > containerRect.right) {
          container.scrollLeft += rowRect.right - containerRect.right
        }
      })
    })
  }, [id])

  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}

    // setCellEditor(colId, json) : json = {type, value?, maxSize?, placeholder?, items?, proxyLoader?,
    // callback?} — pose un éditeur propre à CETTE cellule (prioritaire, pour l'édition et
    // get()/getJson(), sur l'éditeur par défaut de la colonne), jusqu'à retrait explicite (json == null).
    // `proxyLoader`/`callback` : combo dynamique (type Combo) — voir DynamicComboCellEditor.
    const makeSetCellEditor = rowId => (colId, json) => {
      const col = leafColumnsRef.current.find(c => c.code === colId)
      if (!col) throw new Error(`setCellEditor: colonne "${colId}" introuvable`)
      if (json == null) {
        setCellEditorOverridesSynced(o => {
          if (!o[rowId]) return o
          // eslint-disable-next-line no-unused-vars
          const { [colId]: _removed, ...restCols } = o[rowId]
          // eslint-disable-next-line no-unused-vars
          const { [rowId]: _removedRow, ...rest } = o
          return Object.keys(restCols).length ? { ...rest, [rowId]: restCols } : rest
        })
        return
      }
      const { type, value, maxSize, placeholder, items, proxyLoader, callback } = json
      setCellEditorOverridesSynced(o => ({
        ...o,
        [rowId]: { ...(o[rowId] ?? {}), [colId]: { type, maxSize, placeholder, items, proxyLoader, callback } },
      }))
      // value fourni : réécrit la donnée stockée sous le nouveau type, pour que get()/getJson() ne
      // restent pas avec une valeur façonnée pour l'ancien type (ex. objet Date après passage en Combo).
      if (value !== undefined) {
        const effectiveCol = type != null ? { ...col, type } : col
        setRowsSynced(prev => prev.map(r => r._rowId === rowId ? { ...r, [colId]: normalizeCellValue(effectiveCol, value) } : r))
      }
    }

    // Proxy d'une ligne identifié par son _rowId (stable après setRows)
    const makeRowProxyById = (rowId) => ({
      get:         colId => {
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, rowId, col)
        return normalizeCellValue(effectiveCol, rowsRef.current.find(r => r._rowId === rowId)?.[colId] ?? null)
      },
      set:         (colId, val) => {
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, rowId, col)
        setRowsSynced(prev => prev.map(r => r._rowId === rowId ? { ...r, [colId]: normalizeCellValue(effectiveCol, val) } : r))
      },
      getData:     () => {
        const row = rowsRef.current.find(r => r._rowId === rowId)
        return Object.fromEntries(leafColumnsRef.current.map(col => [col.code, row?.[col.code] ?? null]))
      },
      getId:       () => rowId,
      getRowIndex: () => rowsRef.current.findIndex(r => r._rowId === rowId),
      getTable:    () => window.tbsComponents[id],
      setCellEditor: makeSetCellEditor(rowId),
      addClassCss:    names => addRowClassCss(rowId, names),
      removeClassCss: names => removeRowClassCss(rowId, names),
    })
    // Proxy d'une ligne par index (pour getRowAt / getSelectedRow)
    const makeRowProxy = idx => ({
      get:         colId => {
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const rowId = rowsRef.current[idx]?._rowId
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, rowId, col)
        return normalizeCellValue(effectiveCol, rowsRef.current[idx]?.[colId] ?? null)
      },
      set:         (colId, val) => {
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const rowId = rowsRef.current[idx]?._rowId
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, rowId, col)
        setRowsSynced(prev => {
          const next = [...prev]
          if (next[idx]) next[idx] = { ...next[idx], [colId]: normalizeCellValue(effectiveCol, val) }
          return next
        })
      },
      getData:     () => Object.fromEntries(leafColumnsRef.current.map(col => [col.code, rowsRef.current[idx]?.[col.code] ?? null])),
      getId:       () => rowsRef.current[idx]?._rowId ?? null,
      getRowIndex: () => idx,
      getTable:    () => window.tbsComponents[id],
      setCellEditor: (colId, json) => makeSetCellEditor(rowsRef.current[idx]?._rowId)(colId, json),
      addClassCss:    names => addRowClassCss(rowsRef.current[idx]?._rowId, names),
      removeClassCss: names => removeRowClassCss(rowsRef.current[idx]?._rowId, names),
    })

    window.tbsComponents[id] = {
      type: 'table',
      ...createGeneralApi(id, userDataRef.current),
      // Cf. commentaire sur scrollElRef plus haut — cible la zone de défilement, pas [data-tbs-root].
      setHeight: height => { const el = scrollElRef.current; if (el) el.style.height = toCssLength(height) },
      getHeight: ()      => scrollElRef.current ? scrollElRef.current.offsetHeight : undefined,
      getData:       () => rowsRef.current,
      setData:       data => setRowsSynced(data),
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
      setTitle:      t => setTitle(t),

      // Colonnes
      setColumnLabel:     (code, lbl)      => setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], label: lbl } })),
      setColumnWidth:     (code, width)    => {
        setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], width, columnWeight: undefined } }))
        // eslint-disable-next-line no-unused-vars
        setColumnSizing(s => { const { [code]: _, ...rest } = s; return rest })
      },
      setColumnWeight:    (code, weight)   => {
        setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], columnWeight: weight, width: undefined } }))
        // eslint-disable-next-line no-unused-vars
        setColumnSizing(s => { const { [code]: _, ...rest } = s; return rest })
      },
      // En plus de colOverrides (structurel — pilote visibleCols, donc largeur/lockedOffsets/pied
      // de tableau), répercute aussi sur columnVisibility (état TanStack réellement consulté par
      // getVisibleLeafColumns()/getHeaderGroups()/getVisibleCells()) — sans quoi la colonne resterait
      // effectivement affichée (hideColumn) ou ne réapparaîtrait jamais (showColumn sur une colonne
      // JSON `visible:false`), tout en désynchronisant lockedOffsets d'une colonne figée suivante.
      showColumn:         code             => {
        setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], visible: true } }))
        setColumnVisibilitySynced(v => ({ ...v, [code]: true }))
      },
      hideColumn:         code             => {
        setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], visible: false } }))
        setColumnVisibilitySynced(v => ({ ...v, [code]: false }))
      },
      // Deux mécanismes déterminent la visibilité réellement rendue d'une colonne : structurel
      // (JSON `visible`/showColumn()/hideColumn()) et bascule utilisateur (panneau "Colonnes ▾").
      // Les deux finissent par écrire dans le même état TanStack `columnVisibility` (ci-dessus) —
      // `colOverrides` reste la source de vérité du côté structurel (seule la visibilité JSON
      // initiale, ou un showColumn()/hideColumn() ultérieur, la modifie), afin qu'une colonne
      // JSON `visible:false` reste listée — et réactivable depuis le panneau — même si rien n'a
      // encore appelé showColumn() pour elle.
      isColumnVisible:    code             => {
        const col = leafColumnsRef.current.find(c => c.code === code)
        if (!col) throw new Error(`isColumnVisible: colonne "${code}" introuvable`)
        const structurallyVisible = { ...col, ...(colOverridesRef.current[code] ?? {}) }.visible !== false
        return structurallyVisible && columnVisibilityRef.current[code] !== false
      },
      // Défile horizontalement `.tableScroll` (scrollElRef) pour amener la colonne `code` dans la
      // zone visible — no-op si elle est figée (`col.lock`, toujours visible) ou actuellement
      // masquée (aucun <th>/<td data-tbs-col> dans le DOM). `querySelector` (singulier) sur
      // `[data-tbs-col]` retombe naturellement sur le <th> d'en-tête (présent même sans ligne de
      // données, précède les <td> dans l'ordre du DOM — un <td> par ligne porte aussi cet
      // attribut). Détection de la bande figée via `th.style.left` (inline, posé seulement par
      // `stickyTh` pour les colonnes `lock`/le préfixe drag/rownum) — **pas** `position: sticky`
      // en `getComputedStyle` : la classe CSS `.th` pose `position: sticky; top: 0` sur TOUS les
      // en-têtes (fige la ligne d'en-tête verticalement au scroll), ce qui aurait fait détecter
      // chaque colonne comme "figée horizontalement" et rendu cette méthode inopérante dans tous
      // les cas (bug constaté). La bande figée couvre visuellement le bord gauche du conteneur
      // sans déplacer son `scrollLeft` : on la traite donc comme un bord gauche effectif, pour ne
      // jamais scroller une colonne juste sous elle (visible sur la seule rect, mais masquée par
      // les colonnes figées qui flottent par-dessus).
      scrollToColumn:     code             => {
        const col = leafColumnsRef.current.find(c => c.code === code)
        if (!col) throw new Error(`scrollToColumn: colonne "${code}" introuvable`)
        if (col.lock) return
        const container = scrollElRef.current
        const cellEl = container?.querySelector(`[data-tbs-col="${code}"]`)
        if (!container || !cellEl) return
        const containerRect = container.getBoundingClientRect()
        let lockedRight = containerRect.left
        container.querySelectorAll('th').forEach(th => {
          if (th.style.left !== '') lockedRight = Math.max(lockedRight, th.getBoundingClientRect().right)
        })
        const cellRect = cellEl.getBoundingClientRect()
        if (cellRect.left < lockedRight) {
          container.scrollLeft -= lockedRight - cellRect.left
        } else if (cellRect.right > containerRect.right) {
          container.scrollLeft += cellRect.right - containerRect.right
        }
      },
      setColumnReadOnly:  code             => setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], mode: 'read' } })),
      setColumnReadWrite: code             => setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], mode: 'write' } })),
      setColumnSortable:  (code, sortable) => setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], sort: sortable } })),
      setColumnTooltip:   (code, tooltip)  => setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], tooltipText: tooltip } })),
      setColumnRenderer:  (code, fn)       => setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], renderer: fn } })),
      setSummaryRenderer: (code, fn)       => setColOverridesSynced(o => ({ ...o, [code]: { ...o[code], summaryRenderer: fn } })),
      setColumnFilter:    (code, value)    => setColumnFilters(prev => {
        const rest = prev.filter(f => f.id !== code)
        const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
        return empty ? rest : [...rest, { id: code, value }]
      }),
      clearColumnFilter:  code             => setColumnFilters(prev => prev.filter(f => f.id !== code)),
      clearAllColumnFilter: ()             => setColumnFilters([]),
      clearFilter:        ()               => setColumnFilters([]), // alias de clearAllColumnFilter()
      // Même geste que le panneau toolbar "Grouper par" : changer/annuler le groupement replie tous
      // les groupes ouverts (setExpanded({})), même motif que l'onChange/le bouton ✕ — sauf si
      // `expanded` est truthy (setGrouping seulement, coercition large — `!!v`, même convention que
      // setVisible()/tbsGeneralApi.js — pas `=== true` strict, qui échouerait sur "true"/1 en
      // provenance de code métier faiblement typé), qui affiche au contraire tous les groupes
      // développés d'emblée (même valeur `true` que l'état initial par défaut, `collapseGrouping:
      // false` — cf. plus haut). `codes` accepte un code unique (rétrocompatible, `string`) ou un
      // tableau de codes pour un regroupement sur plusieurs colonnes (SQL-like `GROUP BY c1, c2`,
      // un seul niveau "à plat" — jamais de hiérarchie imbriquée, cf. `effectiveGrouping`/
      // `getGroupingValue` plus bas), même sélection multiple que le panneau toolbar "Grouper par"
      // (cases à cocher).
      setGrouping:        (codes, expanded) => { setGrouping(Array.isArray(codes) ? codes : (codes ? [codes] : [])); setExpanded(expanded ? true : {}) },
      clearGrouping:      ()               => { setGrouping([]); setExpanded({}) },
      // Même geste qu'un clic sur l'en-tête de colonne (getToggleSortingHandler) : remplace tout
      // tri existant par ce seul critère. `dir` : "ASC"/"DESC" (insensible à la casse, défaut
      // "ASC" si absent) — même convention que l'attribut JSON `sortDir`.
      setSort:            (code, dir)      => { setSorting([{ id: code, desc: (dir ?? 'ASC').toUpperCase() === 'DESC' }]); if (paging) setCurrentPage(0) },
      clearSort:          ()               => { setSorting([]); if (paging) setCurrentPage(0) },

      // Toolbar
      showToolbar:        ()                            => setToolbarVisible(true),
      hideToolbar:        ()                            => setToolbarVisible(false),
      addElemToolbar:     (btnId, icon, tip, vis, act) => setToolbarStateSynced(s => [...s, { id: btnId, icon, tooltip: tip, visible: vis, active: act }]),
      removeElemToolbar:  btnId                         => setToolbarStateSynced(s => s.filter(b => b.id !== btnId)),
      disableElemToolbar: btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: false } : b)),
      enableElemToolbar:  btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: true }  : b)),
      showElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: true }  : b)),
      hideElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: false } : b)),
      getListOfToolbarBtns: () => toolbarStateRef.current.map(b => b.id),

      // Lignes
      getNbRows:           () => rowsRef.current.length,
      getSelectedRowIndex: () => selectedRowIdxRef.current ?? -1,
      setSelectedRow:      (rowNum, fireEvent) => { selectRow(rowNum); if (fireEvent) onEvent(id, 'onGridSelRow', rowNum, null) },
      getSelectedRow:      () => {
        const idx = selectedRowIdxRef.current
        return (idx == null || idx < 0 || idx >= rowsRef.current.length) ? null : makeRowProxy(idx)
      },
      getRowAt:            rowNum => (rowNum < 0 || rowNum >= rowsRef.current.length) ? null : makeRowProxy(rowNum),
      getRowById:          rowId => {
        const idx = rowsRef.current.findIndex(r => r._rowId === rowId)
        return idx < 0 ? null : makeRowProxy(idx)
      },
      getCellValue:        (rowNum, colName)        => {
        const col = leafColumnsRef.current.find(c => c.code === colName)
        const rowId = rowsRef.current[rowNum]?._rowId
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, rowId, col)
        return normalizeCellValue(effectiveCol, rowsRef.current[rowNum]?.[colName] ?? null)
      },
      setCellValue:        (rowNum, colName, value, fireEvent) => {
        if (rowNum < 0 || rowNum >= rowsRef.current.length) return
        const col = leafColumnsRef.current.find(c => c.code === colName)
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, rowsRef.current[rowNum]._rowId, col)
        const nv = normalizeCellValue(effectiveCol, value)
        setRowsSynced(prev => { const next = [...prev]; next[rowNum] = { ...next[rowNum], [colName]: nv }; return next })
        if (fireEvent) onEvent(id, 'onCellEdit', rowNum, colName)
      },
      // Nouveau format : line = { id (obligatoire → _rowId), colName: val, ... }
      addRow: (beforeRowNum, line, fireEvent) => {
        const { id: rowId = generateId(), ...colVals } = line ?? {}
        const newRow = { _rowId: rowId }
        leafColumnsRef.current.forEach(col => { newRow[col.code] = normalizeCellValue(col, colVals[col.code] ?? null) })
        let pos
        setRowsSynced(prev => {
          const next = [...prev]
          pos = beforeRowNum < 0 ? next.length : beforeRowNum
          next.splice(pos, 0, newRow)
          return next
        })
        if (fireEvent) onEvent(id, 'onRowAdd')
        // Sélectionne + fait défiler jusqu'à la ligne ajoutée — sans ça, un tri actif (JSON ou
        // choisi par l'utilisateur) peut la faire apparaître n'importe où visuellement, donnant
        // l'impression qu'elle n'a pas été ajoutée. `row.index` (TanStack) reste stable sur la
        // position d'origine dans le tableau de données malgré le tri, donc `pos` (index brut
        // d'insertion) correspond bien à l'index à sélectionner.
        selectRow(pos)
        scrollRowIntoView(rowId)
        return makeRowProxyById(rowId)
      },
      addRows: (beforeRowNum, linesList, fireEvent) => {
        const newRows = (linesList ?? []).map(line => {
          const { id: rowId = generateId(), ...colVals } = line ?? {}
          const r = { _rowId: rowId }
          leafColumnsRef.current.forEach(col => { r[col.code] = normalizeCellValue(col, colVals[col.code] ?? null) })
          return r
        })
        let pos
        setRowsSynced(prev => {
          const next = [...prev]
          pos = beforeRowNum < 0 ? next.length : beforeRowNum
          next.splice(pos, 0, ...newRows)
          return next
        })
        if (fireEvent) newRows.forEach(() => onEvent(id, 'onRowAdd'))
        // Sélectionne + révèle la première des lignes ajoutées — même raisonnement que addRow().
        if (newRows.length > 0) {
          selectRow(pos)
          scrollRowIntoView(newRows[0]._rowId)
        }
      },
      deleteRow: (rowId, fireEvent) => {
        const idx = rowsRef.current.findIndex(r => r._rowId === rowId)
        if (idx < 0) return
        setRowsSynced(prev => prev.filter(r => r._rowId !== rowId))
        const prevSel = selectedRowIdxRef.current
        selectRow(prevSel === idx ? null : (prevSel != null && prevSel > idx ? prevSel - 1 : prevSel))
        if (fireEvent) onEvent(id, 'onRowDelete', rowId)
      },
      moveRow: (rowId, newLineNo) => {
        setRowsSynced(prev => {
          const srcIdx = prev.findIndex(r => r._rowId === rowId)
          if (srcIdx < 0) return prev
          const row = prev[srcIdx]
          const next = [...prev]
          next.splice(srcIdx, 1)
          let pos = newLineNo < 0 ? next.length : newLineNo
          // Groupement actif ("Grouper par") : une ligne ne doit pas être déplacée hors de son
          // groupe — clamp de la position cible sur la plage (dans `next`, après retrait de la
          // ligne source) des lignes partageant la même valeur de groupement. Même formatage que
          // TanStack pour comparer (`fmtSummaryValue` pour Date/DateTime, `String()` sinon — voir
          // `getGroupingValue`/`groupBy` dans colDefs). Ligne seule dans son groupe (aucune autre
          // ligne partageant sa valeur) : rien à borner, `pos` reste tel quel. Groupement sur
          // plusieurs colonnes (`grouping` peut désormais contenir plusieurs codes, cf. panneau
          // toolbar "Grouper par" à cases à cocher) : la clé de comparaison combine la valeur
          // formatée de CHAQUE colonne groupée (jointure par un séparateur non ambigu) — une ligne
          // ne peut changer de groupe sur aucun des niveaux actifs.
          const groupCodes = groupingRef.current
          if (groupCodes.length > 0) {
            const keyOf = r => groupCodes.map(code => {
              const groupCol = leafColumnsRef.current.find(c => c.code === code)
              return groupCol ? fmtSummaryValue(groupCol.type, r[code]) : String(r[code])
            }).join('\u0000')
            const groupKey = keyOf(row)
            const sameGroupIdx = []
            next.forEach((r, i) => { if (keyOf(r) === groupKey) sameGroupIdx.push(i) })
            if (sameGroupIdx.length > 0) {
              pos = Math.min(Math.max(pos, sameGroupIdx[0]), sameGroupIdx[sameGroupIdx.length - 1] + 1)
            }
          }
          next.splice(pos, 0, row)
          return next
        })
      },
      dupRow: (rowId, newLineNo, fireEvent) => {
        const srcRow = rowsRef.current.find(r => r._rowId === rowId)
        if (!srcRow) return null
        const newId = generateId()
        const newRow = { ...srcRow, _rowId: newId }
        setRowsSynced(prev => {
          const next = [...prev]
          const pos = newLineNo < 0 ? next.length : newLineNo
          next.splice(pos, 0, newRow)
          return next
        })
        if (fireEvent) onEvent(id, 'onRowAdd')
        return makeRowProxyById(newId)
      },
      removeSelectedRow: (fireEvent) => {
        const idx = selectedRowIdxRef.current
        if (idx == null || idx < 0 || idx >= rowsRef.current.length) return
        const removed = rowsRef.current[idx]
        setRowsSynced(prev => { const next = [...prev]; next.splice(idx, 1); return next })
        selectRow(null)
        if (fireEvent) onEvent(id, 'onRowDelete', removed?._rowId)
      },
      removeRowAt: (rowNum, fireEvent) => {
        if (rowNum < 0 || rowNum >= rowsRef.current.length) return
        const removed = rowsRef.current[rowNum]
        setRowsSynced(prev => { const next = [...prev]; next.splice(rowNum, 1); return next })
        const prevSel = selectedRowIdxRef.current
        selectRow(prevSel === rowNum ? null : (prevSel != null && prevSel > rowNum ? prevSel - 1 : prevSel))
        if (fireEvent) onEvent(id, 'onRowDelete', removed?._rowId)
      },
      // exceptRows : liste de _rowId à conserver
      removeAllRows: (exceptRows) => {
        if (exceptRows?.length) {
          const keep = new Set(exceptRows)
          const prevSel = selectedRowIdxRef.current
          const kept = rowsRef.current.filter(r => keep.has(r._rowId))
          const selId = prevSel != null ? rowsRef.current[prevSel]?._rowId : null
          const newIdx = prevSel == null ? null : kept.findIndex(r => r._rowId === selId)
          setRowsSynced(prev => prev.filter(r => keep.has(r._rowId)))
          selectRow(newIdx != null && newIdx >= 0 ? newIdx : null)
        } else {
          setRowsSynced([])
          selectRow(null)
        }
      },
      emptySelection: (fireEvent) => {
        selectRow(null)
        if (fireEvent) onEvent(id, 'onGridSelRow', -1, null)
      },
      getColumnsCount: () => leafColumnsRef.current.length + 1, // +1 pour l'id interne
      getColumnList: () => [
        {
          getCode: () => '_rowId',
          getLabel: () => null,
          getColumnIndex: () => 0,
          getType: () => null,
          isReadOnly: () => true,
          setComboEditorItems: () => { throw new Error('setComboEditorItems: la colonne "_rowId" n\'est pas de type Combo') },
          getComboEditorItems: () => { throw new Error('getComboEditorItems: la colonne "_rowId" n\'est pas de type Combo') },
        },
        ...leafColumnsRef.current.map((col, i) => ({
          getCode: () => col.code,
          getLabel: () => col.label ?? null,
          getColumnIndex: () => i + 1, // +1 : _rowId occupe l'indice 0
          getType: () => col.type,
          // Mode effectif — override colOverrides (setColumnReadOnly/setColumnReadWrite) prioritaire
          // sur l'attribut JSON `mode` d'origine. `!== 'write'` (pas `=== 'read'`) : même comparaison
          // que le rendu de cellule (ligne ~1159, `col.mode === 'write'`) et que `validate()` (writeCols,
          // ligne ~1003) — une colonne sans `mode` explicite est donc en lecture par défaut, à l'inverse
          // de ChampField/ComboField (types 2/4) qui défaultent en écriture.
          isReadOnly: () => (colOverridesRef.current[col.code]?.mode ?? col.mode) !== 'write',
          // Remplace la liste d'items de l'éditeur (mode write) — même format que l'attribut JSON
          // `items` de la colonne. Passe par `colOverrides` (même mécanisme que setColumnLabel/
          // setColumnWidth/...), donc répercuté immédiatement sur le rendu (mergedCols → visibleCols
          // → colDefs). `col` (capturé par closure sur leafColumnsRef.current au moment de l'appel de
          // getColumnList()) porte le type d'origine, immuable (pas de setColumnType).
          setComboEditorItems: items => {
            if (col.type !== 'Combo') throw new Error(`setComboEditorItems: la colonne "${col.code}" n'est pas de type Combo`)
            setColOverridesSynced(o => ({ ...o, [col.code]: { ...o[col.code], items } }))
          },
          // Items courants de l'éditeur (override s'il existe, sinon ceux du JSON d'origine) — lu
          // via colOverridesRef pour rester synchrone avec un setComboEditorItems() qui précède.
          getComboEditorItems: () => {
            if (col.type !== 'Combo') throw new Error(`getComboEditorItems: la colonne "${col.code}" n'est pas de type Combo`)
            return colOverridesRef.current[col.code]?.items ?? col.items ?? []
          },
        })),
      ],
      enterCellEdition: (rowNum, colName) => {
        if (rowNum < 0 || rowNum >= rowsRef.current.length) throw new Error(`enterCellEdition: rowNum ${rowNum} hors bornes`)
        const container = document.querySelector(`[data-tbs-root="${id}"]`)
        if (!container) return
        const td = container.querySelector(`[data-tbs-row="${rowNum}"][data-tbs-col="${colName}"]`)
        if (!td) return
        td.scrollIntoView({ block: 'nearest', inline: 'nearest' })
        const input = td.querySelector('input:not([type=hidden]), textarea, select')
        input?.focus()
      },
      reload: (extraParams) => {
        extraParamsRef.current = extraParams
        reloadSeqBaselineRef.current = selectSeqRef.current
        setCurrentPage(paging ? 0 : -1)
        setLoadTick(t => t + 1)
      },
      // Force un nouveau rendu sans recharger les données (ex. après changement d'un état
      // externe lu par un renderer de colonne) — ne rappelle pas onTbsGridLoader
      refresh: () => setRefreshTick(t => t + 1),
      // Sérialisation par type de colonne — cf. cellToJsonStr() (Select → "true"/"false", Date/
      // DateTime → "JJ/MM/AAAA"[ HH24:mm:ss], autres types → toujours une chaîne ou null).
      getJson: () => ({
        total: rowsRef.current.length,
        rows:  rowsRef.current.map(r => [
          toJsonStr(r._rowId),
          ...leafColumnsRef.current.map(col =>
            cellToJsonStr(resolveEffectiveCol(cellEditorOverridesRef.current, r._rowId, col), r[col.code])),
        ]),
      }),
      validate: () => {
        const FORMAT_TYPES = new Set(['Date', 'DateTime', 'Heure', 'Montant', 'Numerique'])
        const merged = leafColumnsRef.current.map(c => ({ ...c, ...(colOverridesRef.current[c.code] ?? {}) }))
        const writeCols = merged.filter(c => c.visible !== false && c.mode === 'write')
        let valid = true
        outer: for (const row of rowsRef.current) {
          for (const col of writeCols) {
            const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, row._rowId, col)
            if (READ_ONLY_EDITOR_TYPES.has(effectiveCol.type)) continue
            const v = row[col.code]
            if (v == null || v === '') continue
            const s = String(v)
            let ok = true
            if (effectiveCol.inputMask) {
              try { ok = new RegExp(`^(?:${effectiveCol.inputMask})$`).test(s) } catch { ok = true }
            }
            if (ok && FORMAT_TYPES.has(effectiveCol.type)) {
              if (effectiveCol.type === 'Date' || effectiveCol.type === 'DateTime')    ok = v instanceof Date && !isNaN(v.getTime())
              else if (effectiveCol.type === 'Heure')                        ok = typeof v === 'number' && !isNaN(v) && v >= 0 && v <= 24
              else if (effectiveCol.type === 'Montant' || effectiveCol.type === 'Numerique') ok = typeof v === 'number' && !isNaN(v)
            }
            if (!ok) { valid = false; break outer }
          }
        }
        setTableError(valid ? null : 'Format de cellule invalide')
        return valid
      },
    }
    return () => { delete window.tbsComponents[id]; clearListeners(id) }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Chargement via onTbsGridLoader — columns stable après le montage, exclu des deps intentionnellement
  useEffect(() => {
    const loadFn = gridLoader || window.onTbsGridLoader
    if (typeof loadFn !== 'function') {
      console.error(`[TableField ${id}] onTbsGridLoader non défini`)
      return
    }
    const activeSortCol = sorting.length > 0 ? sorting[0].id : null
    const activeSortDir = sorting.length > 0 ? (sorting[0].desc ? 'DESC' : 'ASC') : null
    let cancelled = false
    // Si ce (re)chargement a été déclenché par reload(), utilise le seq capturé de façon
    // synchrone à ce moment-là plutôt que celui lu ici (l'effet ne s'exécute qu'au rendu
    // suivant, donc un setSelectedRow() appelé juste après reload() dans le même tick serait
    // sinon déjà inclus dans la valeur de départ).
    const seqAtStart = reloadSeqBaselineRef.current ?? selectSeqRef.current
    reloadSeqBaselineRef.current = null
    setLoading(true)
    ;(async () => {
      try {
        const result = await loadFn(id, currentPage, paging ? pageSize : -1, activeSortCol, activeSortDir, extraParamsRef.current)
        if (cancelled) return
        const leafCols = flattenLeafColumns(columns)
        const converted = (result.rows ?? []).map(row => {
          const obj = { _rowId: row[0] }
          leafCols.forEach((col, i) => { obj[col.code] = normalizeCellValue(col, row[i + 1] ?? null) })
          return obj
        })
        setRowsSynced(converted)
        setTotalRows(result.total ?? converted.length)
        // Ne réinitialise la sélection que si aucun setSelectedRow() externe n'a eu lieu pendant
        // le chargement (race condition sinon : un appel synchrone juste après reload()/montage
        // se ferait silencieusement écraser par ce reset).
        if (selectSeqRef.current === seqAtStart) selectRow(null)
        onEvent(id, 'onLoadTable')
        if (!dataLoadedRef.current) { dataLoadedRef.current = true; onDataLoaded?.() }
      } catch (e) {
        if (!cancelled) {
          console.error(`[TableField ${id}] Échec du chargement (onTbsGridLoader) :`, e)
          onEvent(id, 'onLoadTableError')
          if (!dataLoadedRef.current) { dataLoadedRef.current = true; onDataLoaded?.() }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }

    })()
    return () => { cancelled = true }
    // `paging ? sorting : null` (et non `sorting` seul) : sans pagination, toutes les lignes sont
    // déjà chargées et le tri est entièrement géré côté client par TanStack (`getSortedRowModel`,
    // voir plus bas) — un tri sur une colonne ne doit donc pas redéclencher `onTbsGridLoader`. Avec
    // pagination, le tri doit au contraire porter sur l'ensemble des données côté serveur (seule la
    // page courante est chargée) : un changement de `sorting` doit alors bien redéclencher le
    // chargement (activeSortCol/activeSortDir transmis à `loadFn`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentPage, paging ? sorting : null, loadTick, gridLoader])

  // flattenModelLeafColumns exclut uniquement les feuilles sous un groupe masqué ; les overrides
  // runtime (showColumn/hideColumn) s'appliquent ensuite, indépendamment, sur cette liste de
  // feuilles. `visibleCols` pilote la largeur/le pied de tableau/les colonnes figées — doit donc
  // filtrer sur les deux mécanismes de visibilité (structurel ET `columnVisibility`, état TanStack
  // piloté par showColumn/hideColumn comme par le panneau "Colonnes ▾", cf. isColumnVisible())
  // pour rester exactement alignée sur `table.getVisibleLeafColumns()` — sans quoi une colonne
  // masquée resterait comptée comme absente ici tout en étant toujours rendue (ou l'inverse),
  // désynchronisant largeur et `lockedOffsets` des colonnes figées suivantes.
  const leafColumns = useMemo(() => flattenModelLeafColumns(columns), [columns])
  const mergedCols  = useMemo(() => leafColumns.map(c => ({ ...c, ...(colOverrides[c.code] ?? {}) })), [leafColumns, colOverrides])
  const visibleCols = useMemo(
    () => mergedCols.filter(c => c.visible !== false && columnVisibility[c.code] !== false),
    [mergedCols, columnVisibility]
  )

  const colDefs = useMemo(() => {
    const someHaveWeight = visibleCols.some(c => c.columnWeight != null)
    const prefixPx = (dragDropId ? DRAG_WIDTH : 0) + (rowNumber ? ROWNUM_WIDTH : 0)

    let getSize
    if (fitColumns && !someHaveWeight) {
      // Equal distribution — all columns share container width
      const containerForData = Math.max(100, containerWidth - prefixPx)
      const n = visibleCols.length
      const eq = n > 0 ? Math.max(50, Math.floor(containerForData / n)) : 150
      getSize = () => eq
    } else {
      const hasWeights = someHaveWeight
      const totalWeight = hasWeights ? visibleCols.reduce((s, c) => s + (c.columnWeight ?? 0), 0) : 0
      const fixedWidth = hasWeights
        ? visibleCols.filter(c => c.columnWeight == null).reduce((s, c) => s + (c.width ?? 150), 0)
        : 0
      const availableWidth = Math.max(0, containerWidth - prefixPx - fixedWidth)
      getSize = (col) => {
        if (hasWeights && col.columnWeight != null && availableWidth > 0) {
          return Math.max(50, Math.floor((col.columnWeight / totalWeight) * availableWidth))
        }
        return col.width ?? 150
      }
    }

    const FILTERABLE_TYPES = new Set(['Texte', 'Combo', 'Date', 'Montant', 'Numerique', 'Code'])
    const GROUPABLE_TYPES  = new Set(['Code', 'Combo', 'Date', 'DateTime', 'Heure', 'Montant', 'Numerique', 'Texte'])

    // Colonne feuille — reconstruit à l'identique pour chaque feuille de l'arbre `columns`
    // (`col` = version fusionnée avec les overrides, cf. `visibleByCode`).
    const buildLeafColumnDef = col => {
      const isNum = ['Numerique', 'Montant'].includes(col.type)
      const isMultiSelect = col.filterType === 'multiselect'
      // Date/DateTime sont stockées en objet Date (référence) : le groupement (Map par valeur de
      // clé) et le filtre texte doivent passer par la représentation "JJ/MM/AAAA" formatée plutôt
      // que par l'objet Date brut, sans quoi deux dates identiques ne seraient jamais regroupées
      // (Date !== Date par référence) et le filtre chercherait dans un Date.toString() illisible.
      const isDateLike = col.type === 'Date' || col.type === 'DateTime'
      const fmtDate = col.type === 'DateTime' ? formatFrDateTime : formatFrDate
      return {
        id:          col.code,
        accessorKey: col.code,
        header:      () => (
          // '&nbsp;' en secours : un libellé vide laisserait .thLabel sans aucun contenu texte,
          // qui s'effondre à 0px de hauteur en layout flex — désalignant le filtre sous l'en-tête
          // par rapport aux colonnes à libellé non vide (même ligne d'en-tête).
          <span className="flex items-center gap-1.5" style={{ minWidth: 0 }} title={col.tooltipText || stripHtml(col.label)}>
            {col.icon && <i className={col.icon} aria-hidden="true" style={{ flexShrink: 0 }} />}
            <span className={styles.thLabel} dangerouslySetInnerHTML={{ __html: col.label || '&nbsp;' }} />
          </span>
        ),
        size:        getSize(col),
        minSize:     50,
        enableSorting:    col.sort ?? true,
        // Groupable pour tout type de colonne compatible (GROUPABLE_TYPES) — alimente le
        // sélecteur toolbar "Grouper par" (`groupableCols` plus bas) et autorise `grouping`/
        // l'attribut colonne `groupBy` à porter dessus (TanStack ignore silencieusement un code
        // de colonne non `enableGrouping` dans `grouping`).
        enableGrouping:   GROUPABLE_TYPES.has(col.type),
        enableResizing:   true,
        enableColumnFilter: FILTERABLE_TYPES.has(col.type),
        ...(isNum && { sortingFn: numericSortingFn }),
        // `getGroupingValue` reçoit la ligne de donnée brute (TData), pas un Row TanStack —
        // accès direct à la propriété, jamais `row.getValue()` (absent, ferait planter le
        // calcul du modèle groupé). Groupement multi-colonnes (panneau "Grouper par", plusieurs
        // cases cochées) : seule la 1re colonne cochée (`grouping[0]`) est effectivement transmise
        // à TanStack comme colonne de groupement (`effectiveGrouping` plus bas) — les colonnes
        // suivantes sont affichées côte à côte sur la même ligne de groupement (cf. rendu plus
        // bas), jamais en niveaux imbriqués. Quand cette colonne est l'ancre et que plusieurs
        // colonnes sont cochées, sa valeur de groupement devient la clé composite (jointure des
        // valeurs formatées de chaque colonne cochée, dans l'ordre de coche — même séparateur que
        // le clamp `moveRow` plus haut) : sémantique SQL `GROUP BY c1, c2, ...` en un seul niveau
        // plat plutôt qu'en hiérarchie.
        getGroupingValue: row => {
          if (grouping.length > 1 && grouping[0] === col.code) {
            return grouping.map(code => {
              const gcol = mergedCols.find(c => c.code === code)
              return gcol ? fmtSummaryValue(gcol.type, row[code]) : String(row[code] ?? '')
            }).join('\u0000')
          }
          return isDateLike ? (fmtDate(row[col.code]) ?? '') : row[col.code]
        },
        aggregationFn:  isNum ? 'sum' : 'count',
        filterFn:    isMultiSelect ? 'multiSelect' : (isDateLike ? makeMultiTokenFilterFn(fmtDate) : 'multiToken'),
        meta: { col },
        cell: info => {
          const value = info.getValue()
          const rowId = info.row.original._rowId
          const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, rowId, col)
          const isEditableCol = col.mode === 'write' && !READ_ONLY_EDITOR_TYPES.has(effectiveCol.type)
          // Un renderer personnalisé (setColumnRenderer) s'applique aussi aux colonnes en écriture
          // — pas seulement en lecture seule — mais la cellule doit rester éditable : voir
          // EditableRenderedCell (bascule affichage rendu / éditeur réel au clic-perte de focus).
          if (isEditableCol && typeof effectiveCol.renderer === 'function') {
            return <EditableRenderedCell col={effectiveCol} value={value} rowIndex={info.row.index} onEvent={onEvent} tableId={id} onCellChange={onCellChangeRef} />
          }
          return isEditableCol
            ? <CellEditor col={effectiveCol} value={value} rowIndex={info.row.index} onEvent={onEvent} tableId={id} onCellChange={onCellChangeRef} />
            : <CellDisplay col={effectiveCol} value={value} rowIndex={info.row.index} />
        },
        // summaryRenderer (setSummaryRenderer) doit être appelé même si la colonne n'a pas de
        // summaryType d'origine — computeSummary(undefined, ...) retombe alors sur '' (case
        // default), transmis tel quel en `value` au renderer personnalisé.
        footer: (col.summaryType || col.summaryRenderer)
          ? ({ table }) => computeSummary(col.summaryType, table.getFilteredRowModel().flatRows.filter(r => !r.getIsGrouped()), col.code, col.type)
          : undefined,
      }
    }

    // Colonne de groupement (attribut JSON `children`) — en-tête chapeautant récursivement ses
    // enfants ; jamais triable/filtrable/redimensionnable elle-même (ces attributs, ainsi que
    // `lock`, ne concernent que les feuilles — cf. modelByCode). Un groupe masqué (`visible:
    // false`) est omis entièrement (lui et son sous-arbre) ; une feuille masquée, elle, obtient
    // quand même un colDef (initialement invisible via `columnVisibility`) — cf.
    // flattenModelLeafColumns.
    const modelByCode = Object.fromEntries(mergedCols.map(c => [c.code, c]))
    const buildColumnDefTree = entries => {
      const result = []
      for (const entry of entries ?? []) {
        if (entry.children) {
          if (entry.visible === false) continue
          const childDefs = buildColumnDefTree(entry.children)
          if (childDefs.length === 0) continue
          result.push({
            id: entry.code,
            header: () => (
              <span className="flex items-center justify-center gap-1.5" style={{ minWidth: 0 }} title={entry.tooltipText || stripHtml(entry.label)}>
                {entry.icon && <i className={entry.icon} aria-hidden="true" style={{ flexShrink: 0 }} />}
                <span className={styles.thLabel} dangerouslySetInnerHTML={{ __html: entry.label ?? '' }} />
              </span>
            ),
            columns: childDefs,
          })
        } else {
          const merged = modelByCode[entry.code]
          if (merged) result.push(buildLeafColumnDef(merged))
        }
      }
      return result
    }
    const cols = buildColumnDefTree(columns)

    const prefixCols = []
    if (dragDropId) {
      prefixCols.push({
        id: '__drag__', accessorKey: '__drag__',
        header: () => null, cell: () => null,
        size: DRAG_WIDTH, minSize: DRAG_WIDTH,
        enableSorting: false, enableResizing: false, enableGrouping: false, enableColumnFilter: false,
        meta: { drag: true },
      })
    }
    if (rowNumber) {
      prefixCols.push({
        id: '__rownum__', accessorKey: '__rownum__',
        header: () => '#', cell: () => null,
        size: ROWNUM_WIDTH, minSize: ROWNUM_WIDTH,
        enableSorting: false, enableResizing: false, enableGrouping: false, enableColumnFilter: false,
        meta: { rownum: true },
      })
    }

    const spacer = { id: '__spacer__', accessorKey: '__spacer__', header: () => null, cell: () => null, size: 0, minSize: 0, enableSorting: false, enableResizing: false, enableGrouping: false, enableColumnFilter: false, meta: { spacer: true } }

    return [...prefixCols, ...cols, spacer]
  }, [columns, visibleCols, containerWidth, fitColumns, dragDropId, rowNumber, grouping]) // eslint-disable-line react-hooks/exhaustive-deps

  // Un seul niveau de groupement réellement transmis à TanStack (1re colonne cochée) — le
  // groupement multi-colonnes est rendu "à plat" (valeurs des colonnes suivantes affichées sur la
  // même ligne, cf. getGroupingValue dans buildLeafColumnDef / rendu de la ligne de groupement plus
  // bas), jamais en hiérarchie imbriquée par colonne.
  const effectiveGrouping = useMemo(() => (grouping.length > 0 ? [grouping[0]] : []), [grouping])

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns: colDefs,
    state: { sorting, columnFilters, columnVisibility, grouping: effectiveGrouping, expanded, columnSizing },
    onSortingChange:          (updater) => { setSorting(updater); if (paging) setCurrentPage(0) },
    onColumnFiltersChange:    setColumnFilters,
    onColumnVisibilityChange: setColumnVisibilitySynced,
    onExpandedChange:         setExpanded,
    onColumnSizingChange:     setColumnSizing,
    getCoreRowModel:          getCoreRowModel(),
    getSortedRowModel:        getSortedRowModel(),
    getFilteredRowModel:      getFilteredRowModel(),
    getGroupedRowModel:       getGroupedRowModel(),
    getExpandedRowModel:      getExpandedRowModel(),
    columnResizeMode: 'onChange',
    filterFns: { multiToken: multiTokenFilterFn, multiSelect: multiSelectFilterFn },
    // Par défaut TanStack ('reorder') déplace la colonne groupée en tête de la liste COMPLÈTE des
    // colonnes feuilles dès qu'un groupement est actif — y compris devant les colonnes utilitaires
    // (`__drag__`/`__rownum__`), qui n'ont pourtant aucune raison de bouger. Puisque le rendu de la
    // ligne de groupement (plus bas) place lui-même drag/rownum en tête de façon explicite (comme
    // pour une ligne normale), un déplacement TanStack de la colonne ancre devant eux désynchronise
    // l'ordre des cellules vis-à-vis de l'en-tête (`getHeaderGroups()`, qui suit cet ordre) — colonne
    // ancre affichée en tout premier dans l'en-tête, mais après drag/rownum dans chaque ligne.
    // Désactivé : la colonne groupée reste à sa position déclarée, cohérente partout.
    groupedColumnMode: false,
    // Sans ce flag, TanStack referme tous les groupes ouverts (`_autoResetExpanded()`) à chaque
    // recalcul du modèle groupé — déclenché par tout changement de `data`, y compris un simple
    // déplacement de ligne (`setData()`/drag & drop), pas seulement un changement de `grouping`.
    // Même flag déjà posé côté TreeField.jsx.
    autoResetExpanded: false,
  })

  const hasFooter     = visibleCols.some(c => c.summaryType || c.summaryRenderer)
  // table.getAllLeafColumns() (et non colDefs, qui peut désormais contenir des colonnes de
  // groupement sans enableGrouping propre) — aplati nativement par TanStack.
  const groupableCols = table.getAllLeafColumns().filter(c => c.columnDef.enableGrouping)

  const multiSelectOptions = useMemo(() => {
    const result = {}
    visibleCols.filter(c => c.filterType === 'multiselect').forEach(col => {
      const seen = new Set()
      rows.forEach(row => { const v = row[col.code]; if (v != null && v !== '') seen.add(String(v)) })
      const labelMap = {}
      ;(col.items ?? []).forEach(item => { if (!item[3]) labelMap[String(item[0])] = item[1] })
      result[col.code] = [...seen].sort().map(v => ({ value: v, label: labelMap[v] ?? v }))
    })
    return result
  }, [rows, visibleCols])

  const lockedOffsets = useMemo(() => {
    const offsets = {}
    let left = 0
    if (dragDropId) { offsets['__drag__'] = left; left += DRAG_WIDTH }
    if (rowNumber)  { offsets['__rownum__'] = left; left += ROWNUM_WIDTH }
    for (const col of visibleCols) {
      if (col.lock) {
        offsets[col.code] = left
        left += columnSizing[col.code] ?? col.width ?? 150
      }
    }
    return offsets
  }, [visibleCols, columnSizing, dragDropId, rowNumber])

  const stickyTh = useCallback((colId) => {
    if (!(colId in lockedOffsets)) return {}
    return { position: 'sticky', left: lockedOffsets[colId], zIndex: 3, background: '#f0f4f9' }
  }, [lockedOffsets])

  const stickyTd = useCallback((colId, isSelected, isGrouped, isHovered) => {
    if (!(colId in lockedOffsets)) return {}
    // Même priorité que `.tr:hover .td`/`.trSelected .td` en CSS (fields.module.css) : survol
    // gagne sur le fond "groupe", sélection gagne sur tout.
    const bg = isSelected ? '#e0eaf7' : isHovered ? '#e0eaf7' : isGrouped ? '#edf2fa' : '#fff'
    return { position: 'sticky', left: lockedOffsets[colId], zIndex: 1, background: bg }
  }, [lockedOffsets])

  const handleDragStart = useCallback((e, rowId) => {
    window.__tbsDragContext = { tableId: id, rowId, dragDropId }
    e.dataTransfer.effectAllowed = 'move'
  }, [id, dragDropId])

  const handleDragEnd = useCallback(() => {
    window.__tbsDragContext = null
    setDragOverRowId(null)
    setDragOverPos(null)
  }, [])

  const handleDragOverRow = useCallback((e, rowId) => {
    const ctx = window.__tbsDragContext
    if (!ctx || ctx.dragDropId !== dragDropId) return
    e.preventDefault()
    setDragOverRowId(rowId)
    setDragOverPos(computeDropPosition(e, true))
  }, [dragDropId])

  const handleDropOnRow = useCallback((e, destRowId) => {
    e.preventDefault()
    const ctx = window.__tbsDragContext
    if (!ctx || ctx.dragDropId !== dragDropId) return
    const position = destRowId === null ? 'after' : computeDropPosition(e, true)
    safeInvoke(gridDragDropCallback || window.onTbsGridDragDropCallback, ctx.tableId, id, ctx.rowId, destRowId, position, e.shiftKey)
    setDragOverRowId(null)
    setDragOverPos(null)
    window.__tbsDragContext = null
  }, [id, dragDropId, gridDragDropCallback])

  const totalPages = paging ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1
  const goToPage = (page) => { setCurrentPage(Math.max(0, Math.min(page, totalPages - 1))) }

  const pageOffset = paging ? Math.max(0, currentPage) * pageSize : 0

  // Row numbering computed inline (cheap — avoids useMemo dep array issues)
  let rowNums = null
  if (rowNumber) {
    rowNums = new Map()
    let idx = 0
    for (const row of table.getRowModel().rows) {
      if (!row.getIsGrouped()) rowNums.set(row.id, pageOffset + (++idx))
    }
  }

  // Collapsed state — render only the title bar
  const errorOutline = tableError ? { outline: '2px solid hsl(var(--destructive))' } : undefined

  if (collapsed && collapsible) {
    return (
      <div id={id} data-tbs-root={id} className={cn(styles.field, cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
        <div className={styles.tableCard} onClick={() => setCollapsed(false)} style={{ cursor: 'pointer', ...errorOutline }}>
          <div className={styles.collapseBar}>
            <i className="fa-solid fa-chevron-right fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
            {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
            <span className={styles.collapseBarLabel}>{title}</span>
          </div>
        </div>
        {tableError && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginTop: 4 }}>{tableError}</p>}
      </div>
    )
  }

  // Extraits pour être réutilisés à la fois dans la barre de titre repliable (collapsible : fusionnés
  // sur une seule ligne avec le titre) et dans la toolbar classique (non collapsible : ligne séparée,
  // titre à gauche) — évite la duplication entre les deux mises en page ci-dessous.
  const toolbarButtonsContent = toolbarState.map(btn => (
    <Button
      key={btn.id}
      size="sm"
      variant="ghost"
      title={btn.tooltip}
      disabled={btn.active === false}
      className="tbs-toolbar-btn-size"
      style={{ visibility: btn.visible === false ? 'hidden' : 'visible' }}
      onClick={btn.active !== false ? () => onEvent(id, 'onTblToolbarClick', 0, btn.id) : undefined}
    >
      {btn.icon && <i className={btn.icon} aria-hidden="true" />}
    </Button>
  ))
  // Coche/décoche une colonne "Grouper par" — l'ordre de coche détermine l'ordre de la clé
  // composite (1er coché = 1re colonne ancre pour TanStack, cf. `effectiveGrouping`/
  // `getGroupingValue`), conforme à `GROUP BY c1, c2` SQL ; décocher retire uniquement ce code,
  // sans réordonner les codes restants.
  const toggleGroupingCol = code => {
    setGrouping(g => g.includes(code) ? g.filter(c => c !== code) : [...g, code])
    setExpanded({})
  }
  const groupingColumnsContent = (
    <>
      {/* Toujours rendu (pas conditionné par `paging`/`sorting`) : rappelle onTbsGridLoader avec la
          page/le tri courants (currentPage/sorting déjà dans les deps de l'effet de chargement),
          contrairement à `refresh()` (API tbsComponents, force un re-rendu sans recharger). */}
      <Button size="sm" variant="ghost" title={t('reloadTable')} onClick={() => setLoadTick(v => v + 1)}>
        <RefreshCw size={14} />
      </Button>
      <div style={{ position: 'relative' }} ref={groupPanelRef}>
        <Button size="sm" variant="outline" onClick={() => setShowGroupPanel(v => !v)}>
          <span>{t('groupBy')}{grouping.length > 0 ? ` (${grouping.length})` : ''}</span>
          {grouping.length > 0 && (
            <span
              className="ml-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              onClick={e => { e.stopPropagation(); setGrouping([]); setExpanded({}) }}
            >
              <X size={12} />
            </span>
          )}
        </Button>
        {showGroupPanel && (
          <div className={styles.colPanel}>
            {grouping.length > 0 && (
              <button type="button" className={styles.colPanelReset} onClick={() => { setGrouping([]); setExpanded({}) }}>
                {t('resetGrouping')}
              </button>
            )}
            {groupableCols.length === 0 && <span className={styles.colPanelItem}>—</span>}
            {groupableCols.map(c => (
              <label key={c.id} className={styles.colPanelItem}>
                <input type="checkbox" checked={grouping.includes(c.id)} onChange={() => toggleGroupingCol(c.id)} />
                {stripHtml(c.columnDef.meta.col.label)}
              </label>
            ))}
          </div>
        )}
      </div>
      <div style={{ position: 'relative' }} ref={colPanelRef}>
        <Button size="sm" variant="outline" onClick={() => setShowColPanel(v => !v)}>{t('columns')}</Button>
        {showColPanel && (
          <div className={styles.colPanel}>
            {table.getAllLeafColumns()
              .filter(col => !col.columnDef.meta?.spacer && !col.columnDef.meta?.drag && !col.columnDef.meta?.rownum)
              .map(col => (
                <label key={col.id} className={styles.colPanelItem}>
                  <input type="checkbox" checked={col.getIsVisible()} onChange={col.getToggleVisibilityHandler()} />
                  {stripHtml(col.columnDef.meta?.col?.label) || col.id}
                </label>
              ))
            }
          </div>
        )}
      </div>
    </>
  )

  return (
    <div id={id} data-tbs-root={id} className={cn(styles.field, cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
      <div className={styles.tableCard} ref={containerRef} style={errorOutline}>
        {/* Collapse bar — titre + trigger de repli, toolbar et groupement/colonnes fusionnés sur
            cette même ligne (pas de ligne "toolbar" séparée en dessous, contrairement au cas non
            collapsible) — `stopPropagation` sur ces deux blocs : sans lui, cliquer un bouton/select
            déclencherait aussi le repli de la carte (onClick posé sur toute la ligne). */}
        {collapsible && (
          <div className={`${styles.collapseBar} ${styles.collapseBarExpanded}`} onClick={() => setCollapsed(true)}>
            <i className="fa-solid fa-chevron-down fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
            {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
            <span className={styles.collapseBarLabel}>{title}</span>
            {toolbarVisible && (
              <div className={styles.toolbarLeft} onClick={e => e.stopPropagation()}>{toolbarButtonsContent}</div>
            )}
            <div className={styles.toolbarRight} onClick={e => e.stopPropagation()}>{groupingColumnsContent}</div>
          </div>
        )}

        {/* ── Toolbar (non collapsible uniquement — sinon fusionnée dans le collapse bar ci-dessus) ──
            `toolbarVisible` ne cache que le cadre des boutons `toolbarBtns` (JSON), pas le titre
            ni les contrôles intégrés (reload/grouper par/colonnes) à droite. */}
        {!collapsible && <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            {(title || icon) && (
              <>
                <label className={`${styles.label} flex items-center gap-1.5`}>
                  {icon && <i className={`${icon} text-[hsl(var(--muted-foreground))]`} aria-hidden="true" />}
                  {title}
                </label>
                <span className={styles.toolbarSep} />
              </>
            )}
            {toolbarVisible && toolbarButtonsContent}
          </div>
          <div className={styles.toolbarRight}>{groupingColumnsContent}</div>
        </div>}

        {/* ── Table ───────────────────────────────────────────── */}
        <div ref={scrollElRef} className={styles.tableScroll} style={(resizedHeight != null || fixedHeight)
          ? { height: resizedHeight != null ? `${resizedHeight}px` : toCssLength(fixedHeight), overflowY: 'auto' }
          : undefined}>
          <table className={styles.tbl} style={{ width: '100%', ...(fitColumns ? {} : { minWidth: table.getTotalSize() }) }}>
            {/* table-layout:fixed n'honore que la 1re <tr> en l'absence de <colgroup> ; avec
                un en-tête multi-lignes (groupement), la 1re ligne réelle n'est plus celle des
                feuilles — sans colgroup le navigateur ignorerait les largeurs (redimensionnement
                muet, colonnes réparties à l'identique). */}
            <colgroup>
              {table.getVisibleLeafColumns().map(col => (
                <col key={col.id} style={col.columnDef.meta?.spacer ? undefined : { width: col.getSize() }} />
              ))}
            </colgroup>
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id}>
                  {hg.headers.map(header => {
                    // Colonne feuille non groupée à côté d'un groupe voisin : TanStack lui donne
                    // un en-tête "réel" uniquement à la ligne la plus profonde (celle des feuilles)
                    // ; aux lignes plus hautes, elle n'a qu'une cellule de remplissage vide
                    // (isPlaceholder) pour préserver l'alignement des colonnes — aucun rowSpan ne
                    // relie les deux, ce sont deux `<th>` distincts empilés.
                    if (header.isPlaceholder) {
                      return <th key={header.id} colSpan={header.colSpan} className={styles.thGroup} />
                    }
                    const isSpacer = header.column.columnDef.meta?.spacer
                    const isDrag   = header.column.columnDef.meta?.drag
                    const isRownum = header.column.columnDef.meta?.rownum
                    if (isSpacer) return <th key={header.id} className={styles.thSpacer} />
                    if (isDrag)   return <th key={header.id} className={styles.dragHandleTh} style={{ width: DRAG_WIDTH, ...stickyTh('__drag__') }} />
                    if (isRownum) return <th key={header.id} className={styles.rowNumTh} style={{ width: ROWNUM_WIDTH, ...stickyTh('__rownum__') }}>#</th>
                    // Colonne de groupement (attribut JSON `children`) — en-tête chapeautant,
                    // jamais triable/filtrable/redimensionnable (voir buildColumnDefTree).
                    if (header.subHeaders.length > 0) {
                      const rightmostLeaf = getRightmostLeafHeader(header)
                      return (
                        <th key={header.id} colSpan={header.colSpan} className={styles.thGroup}>
                          <span className="flex items-center justify-center gap-1.5" style={{ whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          <div
                            className={`${styles.resizer} ${rightmostLeaf.column.getIsResizing() ? styles.resizerActive : ''}`}
                            onMouseDown={rightmostLeaf.getResizeHandler()}
                            onTouchStart={rightmostLeaf.getResizeHandler()}
                          />
                        </th>
                      )
                    }
                    return (
                      <th key={header.id} data-tbs-col={header.column.id} colSpan={header.colSpan} className={`${styles.th} ${header.column.id in lockedOffsets ? styles.thLocked : ''}`} style={{ width: header.getSize(), ...stickyTh(header.column.id) }}>
                        <div className={styles.thInner}>
                          <span
                            className={header.column.getCanSort() ? styles.sortable : ''}
                            style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <span style={{ fontSize: '1.1em', marginRight: '0.25em', flexShrink: 0 }}>{{ asc: '↑', desc: '↓' }[header.column.getIsSorted()] ?? ''}</span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                        </div>
                        {header.column.getCanFilter() && (
                          header.column.columnDef.meta?.col?.filterType === 'multiselect'
                            ? <MultiSelectFilter column={header.column} options={multiSelectOptions[header.column.id] ?? []} />
                            : <input
                                className={styles.thFilter}
                                value={header.column.getFilterValue() ?? ''}
                                onChange={e => header.column.setFilterValue(e.target.value)}
                                placeholder={t('filterHint')}
                                onClick={e => e.stopPropagation()}
                              />
                        )}
                        <div
                          className={`${styles.resizer} ${header.column.getIsResizing() ? styles.resizerActive : ''}`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={table.getVisibleLeafColumns().length} className={styles.tdMsg}>Chargement…</td></tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr><td colSpan={table.getVisibleLeafColumns().length} className={styles.tdMsg}>{t('noData')}</td></tr>
              ) : table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  // Identifiant stable de ligne, indépendant de l'ordre de tri/filtre affiché —
                  // permet à addRow()/addRows() de localiser puis faire défiler jusqu'à la ligne
                  // ajoutée, quelle que soit sa position visuelle réelle après tri (voir plus bas).
                  data-tbs-row-id={row.original._rowId}
                  className={[
                    styles.tr,
                    row.getIsGrouped() ? styles.trGrouped : '',
                    !row.getIsGrouped() && row.index === selectedRowIdx ? styles.trSelected : '',
                    dragDropId && dragOverRowId === row.original._rowId && dragOverPos === 'before' ? styles.trDragOverBefore : '',
                    dragDropId && dragOverRowId === row.original._rowId && dragOverPos === 'after'  ? styles.trDragOverAfter  : '',
                    ...(rowClassOverrides[row.original._rowId] ?? []),
                  ].join(' ')}
                  draggable={!!dragDropId}
                  onDragStart={dragDropId ? e => handleDragStart(e, row.original._rowId) : undefined}
                  onDragEnd={dragDropId ? handleDragEnd : undefined}
                  onDragOver={dragDropId ? e => handleDragOverRow(e, row.original._rowId) : undefined}
                  onDragLeave={dragDropId ? e => { if (!e.currentTarget.contains(e.relatedTarget)) { setDragOverRowId(null); setDragOverPos(null) } } : undefined}
                  onDrop={dragDropId ? e => handleDropOnRow(e, row.original._rowId) : undefined}
                  onMouseDown={() => {
                    // Sélection au mousedown (avant le focus natif d'une cellule en écriture et
                    // avant le click d'une cellule en lecture) pour garantir que la ligne est
                    // sélectionnée — et l'event de sélection émis — avant onCellBeforeEdit/onGridCellClicked
                    if (row.getIsGrouped()) return
                    // Ligne déjà sélectionnée (re-clic) : ni changement d'état ni event, évite un
                    // onGridSelRow redondant sans changement de sélection.
                    if (selectedRowIdxRef.current === row.index) return
                    selectRow(row.index)
                    onEvent(id, 'onGridSelRow', row.index, null)
                  }}
                  onDoubleClick={() => {
                    if (row.getIsGrouped()) return
                    onEvent(id, 'onRowDblClicked', row.index, 0)
                  }}
                  onMouseEnter={() => setHoveredRowIdx(row.index)}
                  onMouseLeave={() => setHoveredRowIdx(idx => (idx === row.index ? null : idx))}
                >
                  {(() => {
                    const isGroupRow = row.getIsGrouped()
                    const renderCell = cell => {
                      const isSpacer = cell.column.columnDef.meta?.spacer
                      const isDrag   = cell.column.columnDef.meta?.drag
                      const isRownum = cell.column.columnDef.meta?.rownum
                      if (isSpacer) return <td key={cell.id} className={styles.tdSpacer} />
                      if (isDrag) return (
                        <td key={cell.id} className={styles.dragHandleTd} style={{ width: DRAG_WIDTH, ...stickyTd('__drag__', row.index === selectedRowIdx, isGroupRow, row.index === hoveredRowIdx) }}>
                          <span className={styles.dragHandle}>⠿</span>
                        </td>
                      )
                      if (isRownum) return (
                        <td key={cell.id} className={`${styles.td} ${styles.rowNumTd}`} style={{ width: ROWNUM_WIDTH, ...stickyTd('__rownum__', row.index === selectedRowIdx, isGroupRow, row.index === hoveredRowIdx) }}>
                          {isGroupRow ? '' : (rowNums?.get(row.id) ?? '')}
                        </td>
                      )
                      return (
                        <td key={cell.id}
                          data-tbs-row={row.index}
                          data-tbs-col={cell.column.id}
                          className={`${styles.td} ${cell.column.id in lockedOffsets ? styles.tdLocked : ''}`}
                          style={stickyTd(cell.column.id, row.index === selectedRowIdx, false, row.index === hoveredRowIdx)}
                          onClick={() => {
                            // Index parmi les colonnes réellement rendues (table.getVisibleLeafColumns,
                            // hors utilitaires) — et non `visibleCols` (JSON+overrides, structurel) : une
                            // colonne masquée dans le JSON puis réactivée via le panneau "Colonnes ▾" doit
                            // obtenir un index correct, pas -1 (absente de `visibleCols`).
                            const colIdx = table.getVisibleLeafColumns()
                              .filter(c => !c.columnDef.meta?.spacer && !c.columnDef.meta?.drag && !c.columnDef.meta?.rownum)
                              .findIndex(c => c.id === cell.column.id)
                            onEvent(id, 'onGridCellClicked', row.index, colIdx)
                          }}>
                          {cell.getIsPlaceholder() ? null : flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      )
                    }
                    if (!isGroupRow) return row.getVisibleCells().map(renderCell)
                    // Ligne de groupement — regroupement SQL-like sur une ou plusieurs colonnes
                    // (`grouping`, cf. panneau toolbar "Grouper par" à cases à cocher) : chaque
                    // colonne de données garde sa propre cellule (pas de fusion colSpan sur toute
                    // la ligne). Groupement multi-colonnes : TanStack ne reconnaît qu'une seule
                    // colonne "groupée" par ligne (`row.groupingColumnId`, cf. `effectiveGrouping`
                    // plus haut) — les codes suivants cochés (`grouping[1..]`) sont des colonnes
                    // "normales" pour TanStack, affichées ici explicitement côte à côte sur la
                    // MÊME ligne (valeur constante sur toutes les lignes feuilles du groupe, lue
                    // sur la 1re d'entre elles), plutôt qu'en niveaux imbriqués. Seule la colonne
                    // ancre (`row.groupingColumnId`, 1er code cochée) affiche le bouton ▶/▼ + le
                    // nombre de lignes feuilles. Une colonne portant un summaryType agrégat (ou un
                    // summaryRenderer) affiche sa valeur calculée sur les lignes FEUILLES du groupe
                    // (`row.getLeafRows()` — jamais `row.subRows`, qui en groupement multi-colonnes
                    // ne seraient que les sous-groupes du niveau suivant, pas les lignes de
                    // données) ; toute autre colonne (sans summaryType, ni code de groupement)
                    // reste vide — même esprit que le pied de tableau (`computeSummary`/
                    // `SummaryCellDisplay`, cf. buildLeafColumnDef/tfoot plus haut/bas). Drag handle
                    // et numéro de ligne recherchés explicitement (pas par position) — `visibleCells`
                    // respecte ici l'ordre naturel des colonnes (`groupedColumnMode: false`, cf.
                    // useReactTable plus haut) : sans ce flag, TanStack déplace par défaut la colonne
                    // ancre en tête de la liste COMPLÈTE des colonnes feuilles (devant drag/rownum
                    // inclus), désynchronisant cet ordre de celui de l'en-tête (`getHeaderGroups()`,
                    // qui suit le même ordre que `visibleCells` ici).
                    const visibleCells = row.getVisibleCells()
                    const dragCell     = visibleCells.find(c => c.column.columnDef.meta?.drag)
                    const rownumCell   = visibleCells.find(c => c.column.columnDef.meta?.rownum)
                    const dataCells    = visibleCells.filter(c => !c.column.columnDef.meta?.drag && !c.column.columnDef.meta?.rownum)
                    const leafRows     = row.getLeafRows()
                    // Repli : si la colonne ancre (celle sur laquelle cette ligne est groupée) est
                    // elle-même masquée ("Colonnes ▾"/showColumn/hideColumn), le libellé doit tout
                    // de même rester visible quelque part — affiché dans la 1re cellule de données
                    // visible plutôt que de disparaître (`getAllCells()`, pas seulement
                    // `getVisibleCells()`, pour retrouver sa valeur malgré le masquage).
                    const groupedColVisible = dataCells.some(c => c.column.id === row.groupingColumnId)
                    const fallbackGroupedCell = !groupedColVisible ? row.getAllCells().find(c => c.getIsGrouped()) : null
                    const renderGroupValue = (cell, value, withToggle) => {
                      const col = cell.column.columnDef.meta?.col
                      return (
                        <td key={cell.id} className={styles.td}>
                          {withToggle ? (
                            <button type="button" className={styles.expandBtn} onClick={e => { e.stopPropagation(); row.getToggleExpandedHandler()() }}>
                              {row.getIsExpanded() ? '▼ ' : '▶ '}
                              {col && <CellDisplay col={col} value={value} rowIndex={row.index} />}
                              <span className={styles.groupCount}>({leafRows.length})</span>
                            </button>
                          ) : (
                            col && <CellDisplay col={col} value={value} rowIndex={row.index} />
                          )}
                        </td>
                      )
                    }
                    return [
                      dragCell   && renderCell(dragCell),
                      rownumCell && renderCell(rownumCell),
                      ...dataCells.map((cell, i) => {
                        if (cell.column.id === row.groupingColumnId) return renderGroupValue(cell, cell.getValue(), true)
                        if (i === 0 && fallbackGroupedCell) return renderGroupValue(cell, fallbackGroupedCell.getValue(), true)
                        if (grouping.includes(cell.column.id)) return renderGroupValue(cell, leafRows[0]?.getValue(cell.column.id), false)
                        const col = cell.column.columnDef.meta?.col
                        const hasAgg = col && (col.summaryType || col.summaryRenderer)
                        return (
                          <td key={cell.id} className={`${styles.td} ${hasAgg ? styles.groupAggTd : ''}`}>
                            {hasAgg && <SummaryCellDisplay gridId={id} jsonCol={col} value={computeSummary(col.summaryType, leafRows, col.code, col.type)} />}
                          </td>
                        )
                      }),
                    ]
                  })()}
                </tr>
              ))}
              {/* Drop zone — always rendered when dragDropId is set; accepts drops at end of list */}
              {dragDropId && (
                <tr
                  className={dragOverRowId === '__end__' ? styles.trDragOverAfter : ''}
                  onDragOver={e => {
                    const ctx = window.__tbsDragContext
                    if (!ctx || ctx.dragDropId !== dragDropId) return
                    e.preventDefault()
                    setDragOverRowId('__end__')
                    setDragOverPos('after')
                  }}
                  onDrop={e => handleDropOnRow(e, null)}
                  onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) { setDragOverRowId(v => v === '__end__' ? null : v); setDragOverPos(null) } }}
                >
                  <td colSpan={table.getVisibleLeafColumns().length} className={styles.dropZoneTd} />
                </tr>
              )}
            </tbody>
            {hasFooter && (
              <tfoot>
                {/* Une seule ligne, une cellule par colonne feuille visible (dans l'ordre visuel) —
                    getFooterGroups() renverrait une ligne par niveau de profondeur d'en-tête si des
                    colonnes de groupement existent, mais un résumé ne se groupe jamais visuellement :
                    getVisibleLeafColumns() évite cette complexité inutile ici, tout en restant
                    aligné avec l'en-tête (getHeaderGroups()) et le corps (row.getVisibleCells()) —
                    getAllLeafColumns() incluait à tort les colonnes masquées (showColumn/hideColumn,
                    panneau "Colonnes ▾"), décalant la ligne de synthèse par rapport aux colonnes
                    réellement affichées. */}
                <tr className={styles.tfootTr}>
                  {table.getVisibleLeafColumns().map(col => (
                    <td key={col.id} className={col.columnDef.meta?.spacer ? styles.tdSpacer : styles.tfootTd}>
                      {col.columnDef.footer
                        ? <SummaryCellDisplay gridId={id} jsonCol={col.columnDef.meta?.col ?? {}} value={col.columnDef.footer({ table })} />
                        : null}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* ── Pagination ──────────────────────────────────────── */}
        {paging && (
          <div className={styles.paginationBar}>
            <button type="button" className={styles.pageBtn} onClick={() => goToPage(0)}               disabled={currentPage === 0}>«</button>
            <button type="button" className={styles.pageBtn} onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 0}>‹</button>
            <span className={styles.pageInfo}>
              {t('page', { page: currentPage + 1, total: totalPages })}
              &nbsp;—&nbsp;{t('rows', { n: totalRows })}
            </span>
            <button type="button" className={styles.pageBtn} onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages - 1}>›</button>
            <button type="button" className={styles.pageBtn} onClick={() => goToPage(totalPages - 1)}  disabled={currentPage >= totalPages - 1}>»</button>
          </div>
        )}

        {/* ── Poignée de redimensionnement (attribut `resizable`) — hauteur uniquement ── */}
        {resizable && (
          <div
            onMouseDown={handleResizeMouseDown}
            className={styles.splitterBarRow}
            style={{ position: 'relative', height: 10, cursor: 'row-resize' }}
          >
            <GripHorizontal size={12} className={styles.splitterGrip} />
          </div>
        )}
      </div>
      {tableError && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginTop: 4 }}>{tableError}</p>}
    </div>
  )
}

export default memo(TableField)
